import {
  dashboardEventSchema,
  executionNodeSnapshotPayloadSchema,
  executionRequestSnapshotPayloadSchema,
  jsonObjectSchema,
  jsonValueSchema,
  instanceActivityPayloadSchema,
  providerProgressPayloadSchema,
  queueDeltaPayloadSchema,
  repositoryChatSnapshotPayloadSchema,
  taskCallbackPayloadSchema,
  taskSnapshotPayloadSchema,
} from "@arti/dashboard-events";
import {
  EXECUTION_STATUS_VALUES,
  REPO_TOOL_KIND_VALUES,
  REPO_TOOL_STATUS_VALUES,
  TASK_TIMING_EVENT_KIND_VALUES,
} from "@arti/domain-types";
import { MAX_IDLE_STOP_AFTER_HOURS } from "@arti/domain-types/channelEnvironment";
import {
  actorAvatarSeedSchema,
  actorConsolidationScheduleSchema,
  actorTickIntervalMsSchema,
  resolvableActorModelRefSchema,
} from "@arti/runtime-shared/actorConfiguration";
import { channelEnvironmentEgressPolicySchema } from "@arti/runtime-shared/channelEnvironmentEgress";
import { channelEnvironmentPreviewPortsSchema } from "@arti/runtime-shared/channelEnvironmentPreviewPorts";
import { FEATURE_FLAG_NAMES } from "@arti/runtime-shared/featureFlagRegistry";
import { sandboxNodeCapabilitySchema } from "@arti/runtime-shared/sandboxInventory";
import {
  creatableGitBranchNameSchema,
  gitBranchNameSchema,
} from "@arti/shared-source-control/gitBranchName";
import { REPOSITORY_INDEXING_MODE_VALUES } from "@arti/shared-source-control/indexing/runMode";
import z from "zod";

import {
  mcpResourceSchema,
  mcpResourceScopeSelectionSchema,
} from "./mcpResources";
import { id64, taskSnapshotResponseBaseSchema } from "./shared";

// MARK: Helpers

export const dashboardIdleStopAfterHoursSchema = z
  .number()
  .int()
  .positive()
  .max(MAX_IDLE_STOP_AFTER_HOURS)
  .nullable();

/** Accepts a date as an ISO string or UNIX seconds (number). Transforms to Date. */
const dateInput = z
  .union([z.string(), z.number()])
  .refine(
    (val) => {
      if (typeof val === "number") {
        return Number.isFinite(val);
      }
      return !Number.isNaN(new Date(val).getTime());
    },
    { message: "must be a valid date or UNIX seconds" },
  )
  .transform((val): Date => {
    if (typeof val === "number") {
      return new Date(val * 1000);
    }
    return new Date(val);
  });

/** Trims string; empty string becomes null. */
const optionalString = z
  .string()
  .trim()
  .transform((v) => v || null)
  .nullable()
  .optional();

const scCredentialStatus = z
  .enum(["active", "degraded", "revoked"])
  .default("active");

// MARK: Common

const taskStatusEnum = z.enum([
  "queued",
  "running",
  "succeeded",
  "failed",
  "skipped",
  "canceled",
  "cancelled",
]);

const optionalBooleanQueryString = z
  .enum(["true", "false"])
  .transform((value) => value === "true")
  .optional();

export const dashboardInstancesListQuerySchema = z
  .object({
    includeKilled: optionalBooleanQueryString,
    include_killed: optionalBooleanQueryString,
  })
  .transform(({ includeKilled, include_killed: includeKilledAlias }) => ({
    includeKilled: includeKilled ?? includeKilledAlias ?? false,
  }));

export const dashboardInstanceMessagesQuerySchema = z.object({
  before: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

const channelMessageCursorPayloadSchema = z.object({
  createdAt: z.string().datetime(),
  id: z.string().trim().min(1),
  version: z.literal(1),
});

export type ChannelMessageCursorPayload = z.infer<
  typeof channelMessageCursorPayloadSchema
>;

export const channelMessageCursorSchema = z.string().transform((value, ctx) => {
  try {
    const base64 = value.replaceAll("-", "+").replaceAll("_", "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    return channelMessageCursorPayloadSchema.parse(JSON.parse(atob(padded)));
  } catch {
    ctx.addIssue({
      code: "custom",
      message: "Invalid channel message cursor.",
    });
    return z.NEVER;
  }
});

export function encodeChannelMessageCursor(
  cursor: ChannelMessageCursorPayload,
): string {
  return btoa(JSON.stringify(channelMessageCursorPayloadSchema.parse(cursor)))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/u, "");
}

export const dashboardChannelMessagesParamSchema = z.object({
  channelId: z.string().trim().min(1),
});

export const dashboardChannelMessagesQuerySchema = z.object({
  cursor: channelMessageCursorSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

export const dashboardChannelActorAttributionSchema = z.object({
  avatarSeed: z.string(),
  deletedAt: z.string().datetime().nullable(),
  displayName: z.string(),
  id: z.string(),
});

export const dashboardChannelMessageSchema = z.object({
  channelId: z.string(),
  content: z.string(),
  createdAt: z.string().datetime(),
  id: z.string(),
  sender: dashboardChannelActorAttributionSchema.nullable(),
  senderAvatarSeed: z.string().nullable(),
  senderDisplayName: z.string().nullable(),
  senderId: z.string(),
  senderType: z.enum(["instance", "system", "user"]),
});

export const dashboardActorAttributionsResolveRequestSchema = z
  .object({ ids: z.array(z.string().trim().min(1)).min(1).max(100) })
  .meta({ ref: "DashboardActorAttributionsResolveRequest" });

export const dashboardActorAttributionsResolveResponseSchema = z
  .object({
    actorAttributions: z.array(dashboardChannelActorAttributionSchema),
  })
  .meta({ ref: "DashboardActorAttributionsResolveResponse" });

export const dashboardChannelMessagesResponseSchema = z
  .object({
    actorAttributions: z.array(dashboardChannelActorAttributionSchema),
    messages: z.array(dashboardChannelMessageSchema),
    nextCursor: z.string().nullable(),
  })
  .meta({ ref: "DashboardChannelMessagesResponse" });

export const dashboardActorMemoryLayerSchema = z.enum(["episodic", "semantic"]);

export const dashboardInstanceMemoryQuerySchema = z.object({
  includeSuperseded: z
    .enum(["true", "false"])
    .optional()
    .transform((value) => value === "true"),
  kind: z.string().trim().min(1).optional(),
  layer: dashboardActorMemoryLayerSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
});

export const dashboardInstanceCostQuerySchema = z.object({
  window: z.enum(["1h", "24h", "7d", "all"]).optional().default("24h"),
});

export const dashboardInstanceObservabilityListQuerySchema = z.object({
  limit: z.coerce.number().int().positive().max(200).optional(),
  window_hours: z.coerce
    .number()
    .positive()
    .max(24 * 30)
    .optional(),
});

export const dashboardInstanceObservabilityDiffQuerySchema = z.object({
  a: z.string().min(1),
  b: z.string().min(1),
});

export const dashboardInstanceGrowthDistributionQuerySchema = z.object({
  actorInstanceId: z.string().trim().min(1).optional(),
  windowMs: z.coerce
    .number()
    .int()
    .positive()
    .max(7 * 24 * 60 * 60 * 1000)
    .optional()
    .default(60 * 60 * 1000),
});

export const dashboardInstanceChannelArtifactsQuerySchema = z.object({
  includeProcessScripts: z
    .enum(["true", "false"])
    .optional()
    .transform((value) => value === "true"),
});

export const dashboardChannelArtifactsParamSchema = z.object({
  channelId: z.string().trim().min(1),
});

export const dashboardChannelArtifactParamSchema =
  dashboardChannelArtifactsParamSchema.extend({
    artifactId: z.string().trim().min(1),
  });

export const dashboardChannelArtifactSectionParamSchema =
  dashboardChannelArtifactParamSchema.extend({
    sectionId: z.string().trim().min(1),
  });

/**
 * A section read is bound to the artifact version the caller is looking at.
 * Without it, a client assembling a document from several section reads can
 * silently mix bodies from different edits into a file that never existed.
 */
export const dashboardChannelArtifactSectionQuerySchema = z.object({
  version: z.coerce.number().int().positive().optional(),
});

export const dashboardChannelArtifactsQuerySchema =
  dashboardInstanceChannelArtifactsQuerySchema;

const dashboardChannelArtifactSummarySchema = z.object({
  channel_id: z.string(),
  created_at: z.string().datetime(),
  created_by_instance_id: z.string(),
  current_version: z.number().int().positive(),
  excerpt: z.string(),
  id: z.string(),
  kind: z.string(),
  slug: z.string(),
  status: z.string(),
  title: z.string(),
  type: z.string(),
  updated_at: z.string().datetime(),
});

export const dashboardChannelArtifactsResponseSchema = z
  .object({ artifacts: z.array(dashboardChannelArtifactSummarySchema) })
  .meta({ ref: "DashboardChannelArtifactsResponse" });

export const dashboardChannelArtifactResponseSchema = z
  .object({
    channel_id: z.string(),
    created_by_instance_id: z.string(),
    current_version: z.number().int().positive(),
    id: z.string(),
    kind: z.string(),
    lead: z.string().nullable(),
    sections: z.array(
      z.object({
        char_count: z.number().int().nonnegative(),
        id: z.string(),
        title: z.string(),
      }),
    ),
    slug: z.string(),
    status: z.string(),
    title: z.string(),
    type: z.string(),
    updated_at: z.string().datetime(),
  })
  .meta({ ref: "DashboardChannelArtifactResponse" });

export const dashboardChannelArtifactSectionResponseSchema = z
  .object({
    artifact_id: z.string(),
    artifact_version: z.number().int().positive(),
    body: z.string(),
    char_count: z.number().int().nonnegative(),
    section_id: z.string(),
    title: z.string(),
  })
  .meta({ ref: "DashboardChannelArtifactSectionResponse" });

export const dashboardChannelArtifactVersionsResponseSchema = z
  .object({
    versions: z.array(
      z.object({
        author_instance_id: z.string(),
        created_at: z.string().datetime(),
        id: z.string(),
        op_summary: z.string().nullable(),
        reason: z.string().nullable(),
        version: z.number().int().positive(),
      }),
    ),
  })
  .meta({ ref: "DashboardChannelArtifactVersionsResponse" });

export const dashboardIssueIntegrationOAuthCallbackQuerySchema = z.object({
  code: z.string().min(1).optional(),
  error: z.string().min(1).optional(),
  error_description: z.string().min(1).optional(),
  state: z.string().min(1),
});

export const dashboardIndexingRunsListQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(200).optional(),
});

export const dashboardIndexingRunInsightsQuerySchema = z.object({
  insightType: z.string().max(64).optional(),
  limit: z.coerce.number().min(1).max(200).optional(),
  repositorySlug: z.string().max(256).optional(),
});

export const dashboardFlowStageJournalQuerySchema = z.object({
  cursor: z.string().trim().optional(),
  flow_type: z
    .enum(["plan_revision", "execution", "compile_recording"])
    .optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

const usagePricingDimensionsSchema = z.object({
  cacheStorageTokenHours: z.number().optional(),
  inputTokensByModality: z
    .object({
      audio: z.number().optional(),
      image: z.number().optional(),
      text: z.number().optional(),
      video: z.number().optional(),
    })
    .optional(),
  outputTokensByModality: z
    .object({
      audio: z.number().optional(),
      image: z.number().optional(),
      text: z.number().optional(),
      video: z.number().optional(),
    })
    .optional(),
  uncachedInputTokens: z.number().optional(),
});

const taskUsageCostBreakdownSchema = z.object({
  cacheStorageUsd: z.number().optional(),
  cacheWriteUsd: z.number().optional(),
  cachedInputUsd: z.number().optional(),
  completeness: z.enum(["exact", "partial"]).optional(),
  inputUsd: z.number().optional(),
  missingPricingInputs: z.array(z.string()).optional(),
  outputUsd: z.number().optional(),
  totalUsd: z.number().optional(),
});

const taskUsageTokensSchema = z.object({
  cacheWriteTokens: z.number(),
  cachedInputTokens: z.number(),
  completionTokens: z.number(),
  promptTokens: z.number(),
  totalTokens: z.number(),
});

const taskUsageTotalsSchema = taskUsageTokensSchema
  .merge(usagePricingDimensionsSchema)
  .extend({
    costBreakdown: taskUsageCostBreakdownSchema.optional(),
    costUsd: z.number().optional(),
  });

const taskUsageEntrySchema = usagePricingDimensionsSchema.extend({
  costBreakdown: taskUsageCostBreakdownSchema.optional(),
  costUsd: z.number().optional(),
  entryId: z.string(),
  model: z.string().nullable(),
  provider: z.string(),
  rawUsage: jsonObjectSchema.optional(),
  stage: z.string(),
  threadId: z.string().nullable(),
  timestampMs: z.number(),
  tokens: taskUsageTokensSchema,
});

const usageSummarySnapshotSchema = z.object({
  entries: z.array(taskUsageEntrySchema),
  totals: taskUsageTotalsSchema,
});

export const dashboardTaskSchema = taskSnapshotResponseBaseSchema
  .extend({
    cost_usd: z.number().nullable(),
    current_display_provider: z.string().nullable().optional(),
    current_model_ref: z.string().nullable().optional(),
    current_model_timestamp_ms: z.number().int().nullable().optional(),
    dominant_display_provider: z.string().nullable().optional(),
    dominant_model_ref: z.string().nullable().optional(),
    flow_run_id: z.string().nullable(),
    has_details: z.boolean(),
    organization_id: z.string().nullable(),
    status: taskStatusEnum,
    usage_summary: usageSummarySnapshotSchema.nullable().optional(),
  })
  .meta({ ref: "DashboardTask" });

const dashboardTaskSummarySchema = taskSnapshotResponseBaseSchema
  .extend({
    cost_usd: z.number().nullable(),
    current_display_provider: z.string().nullable().optional(),
    current_model_ref: z.string().nullable().optional(),
    current_model_timestamp_ms: z.number().int().nullable().optional(),
    dominant_display_provider: z.string().nullable().optional(),
    dominant_model_ref: z.string().nullable().optional(),
    flow_run_id: z.string().nullable(),
    has_details: z.boolean(),
    reference_repository_slugs: z.array(z.string()),
    repository_slugs: z.array(z.string()),
    status: taskStatusEnum,
    usage_summary: usageSummarySnapshotSchema.nullable().optional(),
  })
  .meta({ ref: "DashboardTaskSummary" });

const executionStatusEnum = z.enum(EXECUTION_STATUS_VALUES);

export const executionRequestSchema = z
  .object({
    branch_name: z.string().nullable(),
    created_at: z.number(),
    failure_reason: z.string().nullable(),
    id: z.string(),
    issue_key: z.string(),
    organization_id: z.string().nullable(),
    qa_mandate: z.string().nullable(),
    revision_id: z.string(),
    status: executionStatusEnum,
    updated_at: z.number(),
  })
  .meta({ ref: "DashboardExecutionRequest" });

export const executionNodeSchema = z
  .object({
    assigned_node: z.string().nullable(),
    blocking_count: z.number(),
    commitMessage: z.string().nullable(),
    commitSha: z.string().nullable(),
    commitUrl: z.string().nullable(),
    completed_at: z.number().nullable(),
    context_artifacts: z
      .object({
        dispatch_contract: z
          .object({
            attachment_count: z.number(),
            base_revision: z.string().nullable(),
            branch_name: z.string(),
            prior_node_summary_count: z.number(),
            repository_paths_count: z.number(),
            repository_slug: z.string(),
            selected_branch: z.string().nullable(),
            tests_count: z.number(),
          })
          .nullable(),
        explore_artifact: z
          .object({
            architectural_context_summary: z.string().nullable(),
            context_block_line_count: z.number(),
            context_block_title: z.string(),
            file_reference_count: z.number(),
            key_pattern_count: z.number(),
            potential_risk_count: z.number(),
            runner: z.enum(["explore", "architect"]),
          })
          .nullable(),
        plan_contract: z
          .object({
            attachment_count: z.number(),
            dependencies: z.array(z.string()),
            description: z.string().nullable(),
            implementation_details: z.string().nullable(),
            intent: z.string().nullable(),
            issue_key: z.string().nullable(),
            reference_repository_slugs: z.array(z.string()),
            repository_slug: z.string(),
            self_assessed_risk: z.enum(["low", "medium", "high"]).nullable(),
            technical_constraints: z.array(z.string()),
            title: z.string().nullable(),
          })
          .nullable(),
        verification_results: z.array(
          z.object({
            created_at: z.number().nullable().optional(),
            functional_verified: z.boolean(),
            lint_status: z.enum(["clean", "warnings", "errors", "not_run"]),
            quality_verified: z.boolean(),
            ready_for_completion: z.boolean(),
            requirements_checked: z.array(
              z.object({
                category: z.enum(["functional", "non_functional"]),
                criterion_id: z.string().nullable(),
                evidence: z.string(),
                requirement: z.string(),
                satisfied: z.boolean(),
                skip_reason: z.string().nullable(),
                skip_requirement: z.boolean(),
              }),
            ),
            sequence: z.number(),
            skip_reason: z.string().nullable(),
            skip_status_check: z.boolean(),
            tests_status: z.enum([
              "all_passing",
              "some_failing",
              "not_run",
              "no_tests",
            ]),
            type_check_status: z.enum(["passing", "errors", "not_run"]),
          }),
        ),
        verification_seeds: z.array(
          z.object({
            created_at: z.number().nullable().optional(),
            criteria: z.array(
              z.object({
                category: z.enum(["functional", "quality"]),
                criterion_id: z.string(),
                description: z.string(),
                source: z.string(),
                verification_method: z.string(),
              }),
            ),
            override_step: z.number().nullable(),
            sequence: z.number(),
            source: z.enum(["plan", "agent_override"]),
          }),
        ),
      })
      .nullable()
      .optional(),
    dependencies: z.array(z.string()),
    error: z.string().nullable(),
    file_changes: z.array(
      z.object({
        added_lines: z.number().nullable(),
        path: z.string(),
        removed_lines: z.number().nullable(),
        status: z.string(),
        ui_ux_visual_change: z
          .object({
            assets: z.array(z.string()).nullable().optional(),
            notes: z.string().nullable().optional(),
            summary: z.string(),
          })
          .nullable()
          .optional(),
      }),
    ),
    id: z.string(),
    plan_item_id: z.string().nullable(),
    repository_slug: z.string().nullable(),
    request_id: z.string(),
    review_feedback: z.array(
      z.object({
        category: z.enum(["functional", "visual"]).optional(),
        message: z.string(),
        normalized_filepath: z.string().optional(),
        severity: z.enum(["low", "medium", "high"]),
      }),
    ),
    started_at: z.number().nullable(),
    status: executionStatusEnum,
    summary: z.string().nullable(),
    ticket_id: z.string().nullable(),
  })
  .meta({ ref: "DashboardExecutionNode" });

export const dashboardAuthSessionResponseSchema = z
  .object({
    authenticated: z.boolean(),
    email: z.string().optional(),
    expires_at: z.number().nullable().optional(),
    global_role: z.string().nullable().optional(),
    org_role: z.string().nullable().optional(),
    organization_id: z.string().nullable().optional(),
    user: z.object({ email: z.string() }).nullable().optional(),
    user_id: z.string().nullable().optional(),
  })
  .meta({ ref: "DashboardAuthSessionResponse" });

export const dashboardOtpRequestSchema = z
  .object({ email: z.email() })
  .meta({ ref: "DashboardOtpRequest" });

export const dashboardOtpRequestResponseSchema = z
  .object({ sent: z.boolean() })
  .meta({ ref: "DashboardOtpRequestResponse" });

export const dashboardOtpVerifyRequestSchema = z
  .object({
    code: z.string(),
    email: z.email(),
    rememberDevice: z.boolean().optional(),
  })
  .meta({ ref: "DashboardOtpVerifyRequest" });

export const dashboardOtpVerifyResponseSchema = z
  .object({ verified: z.boolean() })
  .meta({ ref: "DashboardOtpVerifyResponse" });

export const dashboardLogoutResponseSchema = z
  .object({ logged_out: z.boolean() })
  .meta({ ref: "DashboardLogoutResponse" });

export const dashboardWaitlistJoinRequestSchema = z
  .object({ company: z.string().optional(), email: z.string().email() })
  .meta({ ref: "DashboardWaitlistJoinRequest" });

export const dashboardWaitlistJoinResponseSchema = z
  .object({ ok: z.literal(true) })
  .meta({ ref: "DashboardWaitlistJoinResponse" });

// MARK: Internal

const dashboardLegacyTaskCallbackRequestSchema = z
  .object({
    correlation_id: z.string().nullable().optional(),
    payload: jsonObjectSchema,
    source: z.string().optional(),
    task_id: z.string().min(1),
    timestamp: z.number().optional(),
    type: z.literal("task_callback"),
    version: z.number().optional(),
  })
  .loose();

export const dashboardInternalEventRequestSchema = z
  .union([dashboardEventSchema, dashboardLegacyTaskCallbackRequestSchema])
  .meta({ ref: "DashboardInternalEventRequest" });

export const dashboardInternalEventResponseSchema = z
  .object({ received: z.literal(true) })
  .meta({ ref: "DashboardInternalEventResponse" });

export const dashboardRealtimeEventTypeValues = [
  "task_callback",
  "task_snapshot",
  "execution_request_snapshot",
  "execution_node_snapshot",
  "compile_recording_snapshot",
  "queue_delta",
  "node_status",
  "plan_revision_updated",
  "provider_progress",
  "repository_chat_snapshot",
  "billing_balance_updated",
  "session_invalidated",
  "organization_membership_changed",
  "instance_spawned",
  "instance_status_changed",
  "instance_activity",
  "actor_instance_changed",
  "channel_changed",
  "template_changed",
  "channel_environment_changed",
] as const;

export const dashboardRealtimeEventTypeSchema = z.enum(
  dashboardRealtimeEventTypeValues,
);

function createDashboardRealtimeEventSchema<
  TType extends z.infer<typeof dashboardRealtimeEventTypeSchema>,
  TPayload extends z.ZodType,
>(type: TType, payload: TPayload) {
  return z.object({
    correlation_id: z.string().nullable().optional(),
    hydration: z
      .object({
        message: z.string().nullable().optional(),
        ok: z.boolean(),
        reason: z.string().nullable().optional(),
      })
      .optional(),
    payload,
    source: z.string(),
    timestamp: z.number(),
    type: z.literal(type),
    version: z.literal(1),
  });
}

const dashboardTaskCallbackRealtimePayloadSchema =
  taskCallbackPayloadSchema.extend({ task: dashboardTaskSchema.optional() });

const dashboardTaskSnapshotRealtimePayloadSchema =
  taskSnapshotPayloadSchema.extend({ task: dashboardTaskSchema.optional() });

const dashboardExecutionRequestRealtimePayloadSchema =
  executionRequestSnapshotPayloadSchema.extend({
    flow: executionRequestSchema.optional(),
  });

const dashboardExecutionNodeRealtimePayloadSchema =
  executionNodeSnapshotPayloadSchema.extend({
    flow: executionRequestSchema.optional(),
    node: executionNodeSchema.optional(),
  });

const dashboardQueueDeltaRealtimePayloadSchema = queueDeltaPayloadSchema.extend(
  {
    flow: executionRequestSchema.optional(),
    node: executionNodeSchema.optional(),
    task: dashboardTaskSchema.optional(),
  },
);

const dashboardPlanRevisionRealtimePayloadSchema = z
  .object({
    flow_status: z.string().nullable().optional(),
    revision_id: z.string(),
    snapshot: z.lazy(() => dashboardPlanSnapshotSchema).optional(),
    story_issue_key: z.string().nullable().optional(),
  })
  .loose();

const dashboardBillingBalanceUpdatedRealtimeEventSchema =
  createDashboardRealtimeEventSchema(
    "billing_balance_updated",
    z.object({
      balance_usd: z.number().finite(),
      organization_id: z.string().min(1),
      reason: z.string().optional(),
      transaction_id: z.string().optional(),
    }),
  );

const dashboardSessionInvalidatedRealtimeEventSchema =
  createDashboardRealtimeEventSchema(
    "session_invalidated",
    z.object({ user_id: z.string().min(1) }),
  );

const dashboardOrganizationMembershipChangedRealtimeEventSchema =
  createDashboardRealtimeEventSchema(
    "organization_membership_changed",
    z.object({
      organization_id: z.string().min(1),
      user_id: z.string().min(1),
    }),
  );

export const dashboardRealtimeEventSchema = z
  .union([
    createDashboardRealtimeEventSchema(
      "task_callback",
      dashboardTaskCallbackRealtimePayloadSchema,
    ),
    createDashboardRealtimeEventSchema(
      "task_snapshot",
      dashboardTaskSnapshotRealtimePayloadSchema,
    ),
    createDashboardRealtimeEventSchema(
      "execution_request_snapshot",
      dashboardExecutionRequestRealtimePayloadSchema,
    ),
    createDashboardRealtimeEventSchema(
      "execution_node_snapshot",
      dashboardExecutionNodeRealtimePayloadSchema,
    ),
    createDashboardRealtimeEventSchema(
      "compile_recording_snapshot",
      z
        .object({
          compile_recording_request_id: z.string(),
          execution_request_id: z.string(),
          flow: executionRequestSchema.optional(),
        })
        .loose(),
    ),
    createDashboardRealtimeEventSchema(
      "queue_delta",
      dashboardQueueDeltaRealtimePayloadSchema,
    ),
    createDashboardRealtimeEventSchema(
      "node_status",
      z
        .object({
          node: z
            .object({ node_id: z.string(), status: z.string().nullable() })
            .optional(),
          node_id: z.string(),
          status: z.string().optional(),
        })
        .loose(),
    ),
    createDashboardRealtimeEventSchema(
      "plan_revision_updated",
      dashboardPlanRevisionRealtimePayloadSchema,
    ),
    createDashboardRealtimeEventSchema(
      "provider_progress",
      providerProgressPayloadSchema,
    ),
    createDashboardRealtimeEventSchema(
      "repository_chat_snapshot",
      repositoryChatSnapshotPayloadSchema,
    ),
    dashboardBillingBalanceUpdatedRealtimeEventSchema,
    dashboardSessionInvalidatedRealtimeEventSchema,
    dashboardOrganizationMembershipChangedRealtimeEventSchema,
    createDashboardRealtimeEventSchema(
      "instance_spawned",
      z.object({
        avatar_seed: z.string(),
        channels: z.array(
          z.object({ display_name: z.string(), id: z.string() }),
        ),
        display_name: z.string(),
        instance_id: z.string(),
        organization_id: z.string(),
        status: z.string(),
      }),
    ),
    createDashboardRealtimeEventSchema(
      "instance_status_changed",
      z.object({
        current_run_id: z.string().nullable().optional(),
        instance_id: z.string(),
        organization_id: z.string(),
        previous_status: z.string(),
        status: z.string(),
        wake_blocked_at: z.string().datetime().nullable().optional(),
        wake_blocked_consecutive_failure_count: z
          .number()
          .int()
          .nonnegative()
          .nullable()
          .optional(),
        wake_blocked_error: z.string().nullable().optional(),
        wake_blocked_reason: z.string().nullable().optional(),
        wake_blocked_trigger_channel_message_id: z
          .string()
          .nullable()
          .optional(),
        wake_blocked_until: z.string().datetime().nullable().optional(),
      }),
    ),
    createDashboardRealtimeEventSchema(
      "instance_activity",
      instanceActivityPayloadSchema,
    ),
    createDashboardRealtimeEventSchema(
      "actor_instance_changed",
      z.object({
        change_type: z.enum(["create", "update", "delete", "lifecycle"]),
        instance_id: z.string(),
        organization_id: z.string(),
        status: z.string().nullable(),
      }),
    ),
    createDashboardRealtimeEventSchema(
      "channel_changed",
      z.object({
        active_teams_binding_id: z.string().nullable(),
        change_type: z.enum([
          "create",
          "update",
          "delete",
          "binding",
          "membership",
          "archive",
          "environment",
        ]),
        channel_id: z.string(),
        organization_id: z.string(),
      }),
    ),
    createDashboardRealtimeEventSchema(
      "template_changed",
      z.object({
        change_type: z.enum(["create", "update", "delete"]),
        organization_id: z.string(),
        template_id: z.string(),
      }),
    ),
    createDashboardRealtimeEventSchema(
      "channel_environment_changed",
      z.object({
        change_type: z.enum([
          "create",
          "update",
          "delete",
          "restart",
          "archive",
          "recover",
          "repository",
        ]),
        channel_environment_id: z.string().nullable(),
        channel_id: z.string(),
        organization_id: z.string(),
      }),
    ),
  ])
  .meta({ ref: "DashboardRealtimeEvent" });

export const dashboardWebSocketTopicValues = ["internalEventTopic"] as const;

export const dashboardWebSocketTopicSchema = z.enum(
  dashboardWebSocketTopicValues,
);

export const dashboardWebSocketClientMessageSchema = z.discriminatedUnion(
  "kind",
  [
    z.object({
      kind: z.literal("subscribe"),
      topic: dashboardWebSocketTopicSchema,
    }),
    z.object({
      kind: z.literal("unsubscribe"),
      topic: dashboardWebSocketTopicSchema,
    }),
  ],
);

export type DashboardRealtimeEvent = z.infer<
  typeof dashboardRealtimeEventSchema
>;
export type DashboardWebSocketTopic = z.infer<
  typeof dashboardWebSocketTopicSchema
>;

// MARK: Query Schemas

export const dashboardTasksListQuerySchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  flowType: z.string().optional(),
  page: z.string().optional(),
  pageSize: z.string().optional(),
  status: z.string().optional(),
  taskType: z.string().optional(),
  ticketId: z.string().optional(),
});

export const dashboardTaskFacetsQuerySchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  flowType: z.string().optional(),
  status: z.string().optional(),
  taskType: z.string().optional(),
  ticketId: z.string().optional(),
});

export const dashboardTaskEventsQuerySchema = z.object({
  after_id: z.string().optional(),
  event_types: z.string().optional(),
  exclude_event_types: z.string().optional(),
  limit: z.string().optional(),
  stream_id: z.string().optional(),
});

export const dashboardUsageQuerySchema = z.object({
  bucketUnit: z.string().optional(),
  from: z.string().optional(),
  includeBreakdowns: z.string().optional(),
  includeTimeseries: z.string().optional(),
  model: z.string().optional(),
  order: z.string().optional(),
  page: z.string().optional(),
  pageSize: z.string().optional(),
  provider: z.string().optional(),
  stage: z.string().optional(),
  taskId: z.string().optional(),
  threadId: z.string().optional(),
  ticketId: z.string().optional(),
  to: z.string().optional(),
});

export const dashboardTimingQuerySchema = z.object({
  from: z.string().optional(),
  kind: z.string().optional(),
  maxDurationMs: z.string().optional(),
  minDurationMs: z.string().optional(),
  modelStage: z.string().optional(),
  name: z.string().optional(),
  order: z.string().optional(),
  page: z.string().optional(),
  pageSize: z.string().optional(),
  phase: z.string().optional(),
  runId: z.string().optional(),
  stepNumber: z.string().optional(),
  streamId: z.string().optional(),
  taskId: z.string().optional(),
  ticketId: z.string().optional(),
  to: z.string().optional(),
  toolName: z.string().optional(),
});

export const dashboardQuotaForecastingQuerySchema = z.object({
  from: z.string().optional(),
  model: z.string().optional(),
  order: z.string().optional(),
  page: z.string().optional(),
  pageSize: z.string().optional(),
  provider: z.string().optional(),
  to: z.string().optional(),
});

export const dashboardSchemaTelemetryQuerySchema = z.object({
  callsite: z.string().optional(),
  from: z.string().optional(),
  hasParsedResult: z.string().optional(),
  hasRawResponse: z.string().optional(),
  model: z.string().optional(),
  page: z.string().optional(),
  pageSize: z.string().optional(),
  provider: z.string().optional(),
  schemaName: z.string().optional(),
  scopeId: z.string().optional(),
  scopeType: z.string().optional(),
  search: z.string().optional(),
  to: z.string().optional(),
  validationStatus: z.string().optional(),
});

export const dashboardSchemaTelemetryScopeSummaryQuerySchema = z.object({
  scopeId: z.string().optional(),
  scopeType: z.string().optional(),
});

export const dashboardPlanSnapshotsRecentQuerySchema = z.object({
  include_tree: z.string().optional(),
  limit: z.string().optional(),
});

export const dashboardPlanSnapshotIncludeTreeQuerySchema = z.object({
  include_tree: z.string().optional(),
});

export const compileRecordingRequestSchema = z
  .object({
    created_at: z.number(),
    docker_compose_path: z.string().nullable(),
    execution_request_id: z.string(),
    failure_reason: z.string().nullable(),
    id: z.string(),
    issue_key: z.string(),
    organization_id: z.string().nullable(),
    qa_mandate: z.string().nullable(),
    recording_artifact_path: z.string().nullable(),
    recording_artifact_url: z.string().nullable(),
    recording_session_id: z.string().nullable(),
    revision_id: z.string(),
    status: executionStatusEnum,
    summary: z.string().nullable(),
    target_base_url: z.string().nullable(),
    task_id: z.string().nullable(),
    updated_at: z.number(),
  })
  .meta({ ref: "DashboardCompileRecordingRequest" });

export const dashboardLogEntrySchema = z
  .object({
    component: z.string(),
    correlation_id: z.string().nullable(),
    created_at: z.number(),
    id: z.string(),
    level: z.enum(["debug", "info", "warn", "error"]),
    message: z.string(),
    metadata: jsonObjectSchema.nullable(),
    node_id: z.string().nullable(),
    scope: z.string().nullable(),
    task_id: z.string().nullable(),
    ticket_id: z.string().nullable(),
  })
  .meta({ ref: "DashboardLogEntry" });

const dashboardFeatureFlagsSchema = z
  .object({ compile_recording: z.boolean() })
  .meta({ ref: "DashboardFeatureFlags" });

const dashboardMcpToolRefSchema = z
  .object({ id: z.string(), tool: z.string() })
  .meta({ ref: "DashboardMcpToolRef" });

const dashboardFlowStageJournalStatusSchema = z.enum([
  "pending",
  "running",
  "completed",
  "failed",
]);

export const dashboardFlowStageJournalListItemSchema = z
  .object({
    anomaly_flags: z.array(z.string()),
    attempt_count: z.number(),
    completed_at: z.number().nullable(),
    drill_refs: z.array(dashboardMcpToolRefSchema),
    error_summary: z.string().nullable(),
    execution_node_id: z.string().nullable(),
    flow_id: z.string(),
    flow_type: z.string(),
    id: z.string(),
    lease_expires_at: z.number().nullable(),
    lease_owner: z.string().nullable(),
    output_ref: z.string().nullable(),
    stage_name: z.string(),
    stage_sequence: z.number(),
    stage_status: dashboardFlowStageJournalStatusSchema,
    started_at: z.number().nullable(),
    task_id: z.string().nullable(),
    updated_at: z.number(),
  })
  .meta({ ref: "DashboardFlowStageJournalListItem" });

export const dashboardFlowStageJournalEntrySchema =
  dashboardFlowStageJournalListItemSchema
    .extend({
      compile_recording_request_id: z.string().nullable(),
      created_at: z.number(),
      flow_id_hash: z.string(),
      idempotency_key: z.string(),
      idempotency_key_hash: z.string(),
      input_hash: z.string(),
      organization_id: z.string().nullable(),
      output_hash: z.string().nullable(),
      plan_revision_request_id: z.string().nullable(),
      story_flow_state_id: z.string().nullable(),
      story_issue_key: z.string().nullable(),
    })
    .meta({ ref: "DashboardFlowStageJournalEntry" });

export const dashboardFlowStageJournalListResponseSchema = z
  .object({
    entries: z.array(dashboardFlowStageJournalListItemSchema),
    next_cursor: z.string().nullable(),
    total_returned: z.number(),
  })
  .meta({ ref: "DashboardFlowStageJournalListResponse" });

export const dashboardFlowStageJournalEntryResponseSchema = z
  .object({ entry: dashboardFlowStageJournalEntrySchema })
  .meta({ ref: "DashboardFlowStageJournalEntryResponse" });

const dashboardExecutionFlowDetailResponseSchema = z
  .object({
    compile_recording_request_ids: z.array(z.string()),
    feature_flags: dashboardFeatureFlagsSchema,
    flow: executionRequestSchema,
    flow_type: z.literal("execution"),
    nodes: z.array(executionNodeSchema),
  })
  .meta({ ref: "DashboardExecutionFlowDetailResponse" });

export const dashboardStartCompileRecordingResponseSchema = z
  .object({
    request_id: z.string().nullable(),
    status: z.enum(["started", "invalid"]),
  })
  .meta({ ref: "DashboardStartCompileRecordingResponse" });

const dashboardCompileRecordingFlowDetailResponseSchema = z
  .object({
    compile_recording: compileRecordingRequestSchema,
    execution_flow: executionRequestSchema.nullable(),
    flow_type: z.literal("compile_recording"),
    logs: z.array(dashboardLogEntrySchema),
    task: dashboardTaskSchema.nullable(),
  })
  .meta({ ref: "DashboardCompileRecordingFlowDetailResponse" });

export const dashboardFlowDetailResponseSchema = z
  .discriminatedUnion("flow_type", [
    dashboardExecutionFlowDetailResponseSchema,
    dashboardCompileRecordingFlowDetailResponseSchema,
  ])
  .meta({ ref: "DashboardFlowDetailResponse" });

export const dashboardFlowLocksResponseSchema = z
  .object({
    generated_at: z.number(),
    locks: z.array(
      z.object({
        active_flow_id: z.string().nullable(),
        active_flow_status: z.string().nullable(),
        active_flow_type: z.string().nullable(),
        lock_expires_at: z.number().nullable(),
        lock_owner: z.string().nullable(),
        story_issue_key: z.string(),
        updated_at: z.number(),
      }),
    ),
    metrics: z.object({
      activeLocks: z.number(),
      activeOwners: z.number(),
      expiringSoon: z.number(),
      staleLocks: z.number(),
      totalLocks: z.number(),
    }),
  })
  .meta({ ref: "DashboardFlowLocksResponse" });

export const dashboardLockReleaseResponseSchema = z
  .object({ released: z.literal(true) })
  .meta({ ref: "DashboardLockReleaseResponse" });

export const dashboardFlowRetryResponseSchema = z
  .object({
    request_id: z.string().nullable(),
    revision_id: z.string(),
    status: z.enum(["no_work", "started"]),
  })
  .meta({ ref: "DashboardFlowRetryResponse" });

// Overview
export const dashboardOverviewResponseSchema = z
  .object({
    generated_at: z.number(),
    nodes: z.array(
      z.object({
        activeTasks: z.number(),
        capacity: z.number(),
        id: z.string(),
        lastAssignment: z.number().nullable(),
        lastCompletion: z.number().nullable(),
        lastFailure: z.number().nullable(),
        lastHeartbeat: z.number().nullable(),
        metadata: jsonObjectSchema.nullable(),
        status: z.string(),
        totalAssignments: z.number(),
      }),
    ),
    plan_revisions: z.array(
      z.object({
        id: z.string(),
        issue_id: z.string().nullable(),
        issue_key: z.string(),
        provider_id: z.string().nullable(),
        provider_type: z.string().nullable(),
        requested_at: z.number(),
        scope: z.enum(["story", "subtasks", "single_subtask"]).nullable(),
        status: z.enum([
          "pending",
          "in_progress",
          "succeeded",
          "failed",
          "error",
          "canceled",
          "cancelled",
          "timed_out",
        ]),
      }),
    ),
    queue: z.object({
      lockedTickets: z.array(z.string()),
      pending: z.array(
        z.object({
          dependsOnTaskId: z.string().nullable(),
          enqueuedAt: z.number(),
          taskId: z.string(),
          ticketId: z.string().nullable(),
        }),
      ),
      pendingCount: z.number(),
    }),
    tasks: z.object({
      recent: z.array(dashboardTaskSummarySchema),
      summary: z.object({
        by_status: z.record(z.string(), z.number()),
        total: z.number(),
        totalCostUsd: z.number().nullable().optional(),
        total_cost_usd: z.number().nullable().optional(),
        total_cost_usd_24h: z.number().nullable().optional(),
      }),
    }),
  })
  .meta({ ref: "DashboardOverviewResponse" });

const dashboardRuntimeEncryptionFingerprintSchema = z
  .object({
    encryptionFingerprint: z
      .string()
      .regex(/^[0-9a-f]{8}$/)
      .nullable(),
  })
  .meta({ ref: "DashboardRuntimeEncryptionFingerprint" });

export const dashboardAdminOverviewResponseSchema =
  dashboardOverviewResponseSchema
    .extend({
      dashboard: dashboardRuntimeEncryptionFingerprintSchema,
      nodes: z.array(
        dashboardOverviewResponseSchema.shape.nodes.element.extend({
          encryptionFingerprint: z
            .string()
            .regex(/^[0-9a-f]{8}$/)
            .nullable(),
        }),
      ),
      orchestrator: dashboardRuntimeEncryptionFingerprintSchema.nullable(),
    })
    .meta({ ref: "DashboardAdminOverviewResponse" });

// Tasks
export const dashboardTasksListResponseSchema = z
  .object({
    entries: z.array(dashboardTaskSummarySchema),
    page: z.number(),
    pageSize: z.number(),
    total: z.number(),
  })
  .meta({ ref: "DashboardTasksListResponse" });

export const dashboardTaskFacetsResponseSchema = z
  .object({
    flowTypeCounts: z.record(z.string(), z.number()),
    statusCounts: z.record(z.string(), z.number()),
  })
  .meta({ ref: "DashboardTaskFacetsResponse" });

export const dashboardTaskCancelRequestSchema = z
  .object({
    task_id: z.string().trim().optional(),
    task_ids: z.array(z.string().trim()).optional(),
  })
  .meta({ ref: "DashboardTaskCancelRequest" });

export const dashboardTaskCancelResponseSchema = z
  .object({
    cancelled_tasks: z.array(z.string()),
    failed_updates: z.array(z.string()),
  })
  .meta({ ref: "DashboardTaskCancelResponse" });

export const dashboardTaskDetailResponseSchema = z
  .object({ task: dashboardTaskSchema })
  .meta({ ref: "DashboardTaskDetailResponse" });

export const dashboardTaskEventSchema = z
  .object({
    created_at: z.number(),
    event_type: z.string(),
    id: z.string(),
    payload: jsonObjectSchema,
    stream_id: z.string().nullable(),
    task_id: z.string(),
  })
  .meta({ ref: "DashboardTaskEvent" });

export const dashboardTaskEventsResponseSchema = z
  .object({
    count: z.number(),
    events: z.array(dashboardTaskEventSchema),
    has_more: z.boolean(),
  })
  .meta({ ref: "DashboardTaskEventsResponse" });

export const dashboardTaskLogsResponseSchema = z
  .object({ logs: z.array(dashboardLogEntrySchema), task_id: z.string() })
  .meta({ ref: "DashboardTaskLogsResponse" });

// Plan Revisions
const dashboardPlanRevisionItemRiskSchema = z.enum(["low", "medium", "high"]);

const dashboardPlanRevisionPlanItemSchema = z.object({
  attachmentRefs: z.array(z.string()).optional(),
  dependencies: z.array(z.string()),
  description: z.string().nullable().optional(),
  id: z.string(),
  implementationDetails: z.string().nullable().optional(),
  intent: z.string().nullable().optional(),
  key: z.string().nullable().optional(),
  referenceRepositorySlugs: z.array(z.string()).optional(),
  repositorySlug: z.string(),
  selfAssessedRisk: dashboardPlanRevisionItemRiskSchema.nullable().optional(),
  sizeSymbol: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  technicalConstraints: z.array(z.string()).optional(),
  title: z.string().nullable().optional(),
  unitOfWorkIds: z.array(z.string()).optional(),
});

const dashboardPlanRevisionPlanItemPartialSchema =
  dashboardPlanRevisionPlanItemSchema.partial();

const dashboardPlanRevisionPlanTreeSchema = z.object({
  selectedBranches: z.record(z.string(), z.string()),
  selectedRepos: z.array(z.string()),
  story: dashboardPlanRevisionPlanItemSchema.nullable(),
  subtasks: z.array(dashboardPlanRevisionPlanItemSchema),
});

const dashboardPlanRevisionPlanDiffSchema = z.object({
  added: z.array(z.string()),
  removed: z.array(z.string()),
  updated: z.array(
    z.object({
      after: dashboardPlanRevisionPlanItemPartialSchema.nullable().optional(),
      before: dashboardPlanRevisionPlanItemPartialSchema.nullable().optional(),
      id: z.string(),
    }),
  ),
});

const dashboardPlanRevisionTouchpointSchema = z.object({
  confidence: z.number().optional(),
  kind: z.enum(["file", "folder"]),
  line_end: z.number().nullable().optional(),
  line_start: z.number().nullable().optional(),
  note: z.string().nullable().optional(),
  path: z.string(),
  repository_slug: z.string(),
  role: z.enum(["modify", "create", "read", "verify"]),
  subtask_id: z.string(),
});

const dashboardPlanRevisionTelemetrySchema = z.object({
  comment_context: z
    .object({ raw_body: z.string().optional(), summary: z.string().optional() })
    .optional(),
  failure: z
    .object({
      reason: z.string(),
      recordedAt: z.string().optional(),
      stage: z.string().optional(),
      status: z.string().optional(),
    })
    .optional(),
  intent: z
    .object({
      confidence: z.number().optional(),
      intent: z.string().optional(),
      notes: z.string().optional(),
      scope: z.enum(["story", "subtasks", "single_subtask"]).optional(),
      should_proceed: z.boolean().optional(),
    })
    .optional(),
  last_snapshot_conflict_at: z.string().optional(),
  refine: z
    .object({
      diff: dashboardPlanRevisionPlanDiffSchema.nullable().optional(),
      planTree: dashboardPlanRevisionPlanTreeSchema.nullable().optional(),
      rawComments: z.array(z.string()).optional(),
      summaries: z.array(z.string()).optional(),
    })
    .optional(),
  snapshot_conflict_count: z.number().optional(),
  story_attachments: z
    .array(
      z.object({
        description: z.string().nullable().optional(),
        filename: z.string(),
        id: z.string(),
        mimeType: z.string().nullable().optional(),
        size: z.number().nullable().optional(),
        url: z.string(),
      }),
    )
    .optional(),
  touchpoints: z
    .object({ touchpoints: z.array(dashboardPlanRevisionTouchpointSchema) })
    .optional(),
  tracker: z
    .object({
      created: z.array(
        z.object({
          error: z.string().optional(),
          issueId: z.string().nullable().optional(),
          issueKey: z.string().nullable().optional(),
          placeholderId: z.string(),
          status: z.enum(["created", "skipped", "failed"]),
        }),
      ),
      dependencies: z.array(
        z.object({
          attempted: z.array(z.string()),
          errors: z.array(z.string()),
          issueKey: z.string().nullable().optional(),
          linked: z.array(z.string()),
          removed: z.array(z.string()),
          skipped: z.array(z.string()),
          supported: z.boolean(),
        }),
      ),
      reason: z.string().optional(),
      status: z.enum(["applied", "skipped"]),
      transitions: z.array(
        z.object({
          changed: z.boolean(),
          desiredState: z.string().nullable().optional(),
          error: z.string().optional(),
          issueKey: z.string().nullable().optional(),
          previousState: z.string().nullable().optional(),
        }),
      ),
    })
    .optional(),
});

export const dashboardPlanRevisionTelemetryResponseSchema = z
  .object({
    id: z.string(),
    issue_key: z.string(),
    telemetry: dashboardPlanRevisionTelemetrySchema.nullable().optional(),
  })
  .meta({ ref: "DashboardPlanRevisionTelemetryResponse" });

export const dashboardPlanRevisionGraphResponseSchema = z
  .object({ tasks: z.array(dashboardTaskSchema) })
  .meta({ ref: "DashboardPlanRevisionGraphResponse" });

// Plan Snapshots
const planSnapshotNodeSchema = z
  .object({
    attachment_refs: z.array(z.string()).nullable(),
    classification: z.string(),
    created_at: z.number(),
    dependencies: z.array(
      z.object({
        depends_on_key: z.string(),
        depends_on_node_id: z.string().nullable(),
      }),
    ),
    description: z.string().nullable(),
    humanized_description: z.string().nullable(),
    is_story: z.boolean(),
    issue_id: z.string().nullable(),
    issue_key: z.string(),
    node_id: z.string(),
    reference_repository_slugs: z.array(z.string()).nullable(),
    repository_slug: z.string().nullable(),
    self_assessed_risk: z.enum(["low", "medium", "high"]).nullable(),
    size_symbol: z.string().nullable(),
    snapshot_id: z.string(),
    sort_order: z.number(),
    status: z.string().nullable(),
    title: z.string().nullable(),
    unit_of_work_ids: z.array(z.string()),
    updated_at: z.number(),
  })
  .meta({ ref: "DashboardPlanSnapshotNode" });

const planSnapshotAttachmentSchema = z
  .object({
    attachment_id: z.string().nullable(),
    created_at: z.number(),
    description: z.string().nullable(),
    filename: z.string(),
    id: z.string(),
    mime_type: z.string().nullable(),
    size: z.number().nullable(),
    snapshot_id: z.string(),
    updated_at: z.number(),
    url: z.string(),
  })
  .meta({ ref: "DashboardPlanSnapshotAttachment" });

const extensivePlanUnitOfWorkSchema = z
  .object({
    acceptance_criteria: z.array(z.string()),
    dependencies: z.array(z.string()),
    description: z.string(),
    estimated_effort: z.enum(["xs", "s", "m", "l", "xl"]).nullable(),
    primary_repository_slug: z.string(),
    reference_repository_slugs: z.array(z.string()),
    snapshot_id: z.string(),
    sort_order: z.number(),
    technical_notes: z.array(z.string()),
    title: z.string(),
    unit_of_work_id: z.string(),
  })
  .meta({ ref: "DashboardExtensivePlanUnitOfWork" });

const extensivePlanSchema = z
  .object({
    integration_notes: z.array(z.string()),
    overview: z.string(),
    plan_complexity_score: z.enum(["low", "medium", "high"]),
    prerequisites: z.array(z.string()),
    reasoning: z.string().nullable(),
    repository_context_primary: z.array(z.string()),
    repository_context_reference: z.array(z.string()),
    trace_id: z.string().nullable(),
    units_of_work: z.array(extensivePlanUnitOfWorkSchema),
  })
  .meta({ ref: "DashboardExtensivePlan" });

export const dashboardPlanSnapshotSchema = z
  .object({
    attachments: z.array(planSnapshotAttachmentSchema).nullable(),
    created_at: z.number(),
    nodes: z.array(planSnapshotNodeSchema).nullable(),
    selected_base_shas: z.record(z.string(), z.string()),
    selected_branches: z.record(z.string(), z.string()),
    selected_repos: z.array(z.string()),
    snapshot_id: z.string(),
    story_issue_id: z.string().nullable(),
    story_issue_key: z.string(),
    uow_plan: extensivePlanSchema.nullable(),
    updated_at: z.number(),
    version: z.number(),
  })
  .meta({ ref: "DashboardPlanSnapshot" });

export const dashboardPlanSnapshotSingleResponseSchema = z
  .object({ snapshot: dashboardPlanSnapshotSchema })
  .meta({ ref: "DashboardPlanSnapshotSingleResponse" });

export const dashboardPlanSnapshotsRecentResponseSchema = z
  .object({
    limit: z.number(),
    snapshots: z.array(dashboardPlanSnapshotSchema),
  })
  .meta({ ref: "DashboardPlanSnapshotsRecentResponse" });

// Agent Mode Budgets
export const dashboardAgentModeBudgetSchema = z
  .object({
    created_at: z.string(),
    max_input_token_budget: z.number().nullable(),
    mode: z.string(),
    phase: z.string(),
    updated_at: z.string(),
  })
  .meta({ ref: "DashboardAgentModeBudget" });

export const dashboardAgentModeBudgetsListResponseSchema = z
  .object({ budgets: z.array(dashboardAgentModeBudgetSchema) })
  .meta({ ref: "DashboardAgentModeBudgetsListResponse" });

export const dashboardAgentModeBudgetUpsertRequestSchema = z
  .object({
    budgets: z
      .array(
        z.object({
          max_input_token_budget: z.number().nullable().optional(),
          mode: z.string(),
          phase: z.string(),
        }),
      )
      .optional(),
    max_input_token_budget: z.number().nullable().optional(),
    mode: z.string(),
    phase: z.string(),
  })
  .loose()
  .meta({ ref: "DashboardAgentModeBudgetUpsertRequest" });

export const dashboardAgentModeBudgetsUpsertResponseSchema = z
  .object({ budgets: z.array(dashboardAgentModeBudgetSchema) })
  .meta({ ref: "DashboardAgentModeBudgetsUpsertResponse" });

// Issue Integrations
export const dashboardIssueProviderSchema = z
  .object({
    base_url: z.string().nullable(),
    cloud_id: z.string().nullable(),
    connect: z.object({
      app_account_id: z.string().nullable(),
      app_account_type: z.string().nullable(),
      app_display_name: z.string().nullable(),
      app_key: z.string().nullable(),
      client_key: z.string().nullable(),
      client_key_original: z.string().nullable(),
      disabled_at: z.number().nullable(),
      enabled_at: z.number().nullable(),
      installed_at: z.number().nullable(),
      last_lifecycle_at: z.number().nullable(),
      last_lifecycle_event: z.string().nullable(),
      last_secret_rotation_at: z.number().nullable(),
      oauth_client_id: z.string().nullable(),
      probe_created_at: z.number().nullable(),
      probe_issue_key: z.string().nullable(),
      probe_token: z.string().nullable(),
      public_key: z.string().nullable(),
      shared_secret_credential_id: z.string().nullable(),
      status: z.string().nullable(),
      uninstalled_at: z.number().nullable(),
    }),
    connect_modules: z
      .object({
        admin_pages: z.array(
          z.object({
            key: z.string().nullable(),
            location: z.string().nullable(),
            name: z.string().nullable(),
            url: z.string().nullable(),
            weight: z.number().nullable(),
          }),
        ),
        raw: jsonObjectSchema.nullable(),
      })
      .nullable(),
    created_at: z.number(),
    default_project: z.string().nullable(),
    display_name: z.string(),
    forge_realtime: z.object({
      cloud_id: z.string().nullable(),
      installation_ari: z.string().nullable(),
      module_key: z.string().nullable(),
      updated_at: z.number().nullable(),
      web_trigger_url: z.string().nullable(),
    }),
    is_enabled: z.boolean(),
    organization_url: z.string().nullable(),
    provider_id: z.string(),
    registration_source: z.string().nullable(),
    site_name: z.string().nullable(),
    source_control_provider_id: z.string().nullable(),
    timeout_ms: z.number().nullable(),
    type: z.enum(["jira"]),
    updated_at: z.number(),
  })
  .meta({ ref: "DashboardIssueProvider" });

export const dashboardIssueCredentialSchema = z
  .object({
    access_token: z.string().nullable(),
    access_token_expires_at: z.number().nullable(),
    client_id: z.string().nullable(),
    client_secret_ref: z.string().nullable(),
    connect: z.object({
      app_key: z.string().nullable(),
      base_url: z.string().nullable(),
      client_key: z.string().nullable(),
      last_rotation_at: z.number().nullable(),
      oauth_client_id: z.string().nullable(),
      product_type: z.string().nullable(),
      public_key: z.string().nullable(),
    }),
    created_at: z.number(),
    credential_id: z.string(),
    kind: z.enum(["pat", "api_token", "oauth", "connect_shared_secret"]),
    last_refreshed_at: z.number().nullable(),
    oauth_force_token_refresh: z.boolean(),
    provider_id: z.string(),
    refresh_token_ref: z.string().nullable(),
    scopes: z.array(z.string()),
    secret_ref: z.string(),
    updated_at: z.number(),
    user_identifier: z.string().nullable(),
  })
  .meta({ ref: "DashboardIssueCredential" });

export const dashboardIssueProjectSchema = z
  .object({
    created_at: z.number(),
    external_key: z.string(),
    name: z.string().nullable(),
    project_id: z.string(),
    provider_id: z.string(),
    subscriptions: z.array(z.string()),
    updated_at: z.number(),
  })
  .meta({ ref: "DashboardIssueProject" });

export const dashboardIssueWebhookSchema = z
  .object({
    algorithm: z.string().nullable(),
    created_at: z.number(),
    encoding: z.string().nullable(),
    header_name: z.string().nullable(),
    last_rotation_at: z.number().nullable(),
    provider_id: z.string(),
    query_param_name: z.string().nullable(),
    secret_hash: z.string().nullable(),
    secret_ref: z.string().nullable(),
    source: z.string().nullable(),
    updated_at: z.number(),
    verification_method: z.string(),
    webhook_id: z.string(),
  })
  .nullable()
  .meta({ ref: "DashboardIssueWebhook" });

export const dashboardIssueProjectCreateRequestSchema = z
  .object({
    external_key: z.string().trim().min(1),
    name: z.string().trim().optional(),
    project_id: z.string().trim().min(1),
    subscriptions: z.array(z.string()).optional(),
  })
  .meta({ ref: "DashboardIssueProjectCreateRequest" });

const dashboardIssueNormalizedStatusSchema = z.enum([
  "To Do",
  "In Progress",
  "Done",
]);

export const dashboardIssuesListQuerySchema = z.object({
  cursor: z.string().trim().min(1).optional(),
  issue_type: z.string().trim().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  parent_key: z.string().trim().min(1).optional(),
  project_key: z.string().trim().min(1).optional(),
  provider_id: z.string().trim().min(1).optional(),
  q: z.string().trim().min(1).max(200).optional(),
  status: z.string().trim().min(1).optional(),
  work_state: z.enum(["active", "all"]).default("active"),
});

export const dashboardIssueKeyParamSchema = z.object({
  issueKey: z.string().trim().min(1),
  providerId: z.string().trim().min(1),
});

export const dashboardIssueListItemSchema = z
  .object({
    assignee: z.string().nullable(),
    issue_id: z.string(),
    issue_key: z.string(),
    issue_type: z.string().nullable(),
    normalized_status: dashboardIssueNormalizedStatusSchema.nullable(),
    parent_key: z.string().nullable(),
    priority: z.string().nullable(),
    project_key: z.string().nullable(),
    provider_id: z.string(),
    status: z.string().nullable(),
    summary: z.string().nullable(),
    updated_at: z.number().nullable(),
    url: z.string().nullable(),
  })
  .meta({ ref: "DashboardIssueListItem" });

export const dashboardIssuesListResponseSchema = z
  .object({
    counts: z.object({
      by_status: z.record(z.string(), z.number()).optional(),
      by_type: z.record(z.string(), z.number()).optional(),
      total: z.number().nullable(),
    }),
    issues: z.array(dashboardIssueListItemSchema),
    page: z.object({
      has_more: z.boolean(),
      next_cursor: z.string().nullable(),
    }),
  })
  .meta({ ref: "DashboardIssuesListResponse" });

export const dashboardIssueDetailSchema = z
  .object({
    assignee: z.string().nullable(),
    comment_count: z.number().nullable(),
    comments: z.array(
      z.object({
        author: z.string().nullable(),
        body: z.string(),
        comment_id: z.string(),
        created_at: z.number(),
        updated_at: z.number(),
      }),
    ),
    delivery_refs: z
      .array(z.object({ id: z.string(), tool: z.string() }))
      .optional(),
    description: z.string().nullable(),
    description_content: z.unknown().nullable(),
    description_etag: z.string().nullable(),
    description_format: z.string().nullable(),
    issue_id: z.string(),
    issue_key: z.string(),
    issue_type: z.string().nullable(),
    labels: z.array(z.string()),
    linked_issue_refs: z.array(
      z.object({
        direction: z.string().nullable(),
        key: z.string().nullable(),
        type: z.string().nullable(),
        url: z.string().nullable(),
      }),
    ),
    normalized_status: dashboardIssueNormalizedStatusSchema.nullable(),
    parent_key: z.string().nullable(),
    priority: z.string().nullable(),
    project_key: z.string().nullable(),
    provider_id: z.string(),
    source_control_provider_id: z.string().nullable(),
    status: z.string().nullable(),
    subtask_keys: z.array(z.string()),
    summary: z.string().nullable(),
    url: z.string().nullable(),
  })
  .meta({ ref: "DashboardIssueDetail" });

export const dashboardIssueDetailResponseSchema = z
  .object({
    issue: dashboardIssueDetailSchema,
    suggested_channel_name: z.string(),
  })
  .meta({ ref: "DashboardIssueDetailResponse" });

const dashboardIssueWorkspaceRepositoryRequestSchema = z.object({
  defaultBranch: z.string().trim().min(1).nullable().optional(),
  permission: z.enum(["read", "write"]),
  providerId: z.string().trim().min(1),
  repositoryId: z.string().trim().min(1),
});

export const dashboardIssueWorkspaceEnvironmentRequestSchema = z.object({
  actorProcessToolsEnabled: z.boolean().default(true),
  cpuCount: z.number().positive(),
  egressPolicy: channelEnvironmentEgressPolicySchema.default({ mode: "deny" }),
  idleStopAfterHours: dashboardIdleStopAfterHoursSchema.default(168),
  maxConcurrentSpawns: z.number().int().positive().default(5),
  maxSpawnWallSeconds: z.number().int().positive().nullable().default(86_400),
  previewPorts: channelEnvironmentPreviewPortsSchema.default([]),
  profileId: z.string().trim().min(1).optional(),
  ramMB: z.number().int().positive(),
  repositories: z
    .array(dashboardIssueWorkspaceRepositoryRequestSchema)
    .max(20)
    .default([]),
  retentionDaysAfterChannelKill: z
    .number()
    .int()
    .positive()
    .nullable()
    .default(30),
  storageMB: z.number().int().positive(),
});

export const dashboardIssueWorkspaceCreateRequestSchema = z
  .object({
    actors: z
      .array(
        z.object({
          displayName: z.string().trim().min(1).max(256),
          templateId: z.string().trim().min(1),
        }),
      )
      .min(1)
      .max(20),
    channelDisplayName: z
      .string()
      .trim()
      .min(1)
      .max(256)
      .regex(/^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/),
    environment: dashboardIssueWorkspaceEnvironmentRequestSchema.nullable(),
    idempotencyKey: z.string().uuid(),
    issueKey: z.string().trim().min(1),
    leadTemplateId: z.string().trim().min(1),
    providerId: z.string().trim().min(1),
  })
  .superRefine((value, context) => {
    const displayNames = value.actors.map((actor) =>
      actor.displayName.toLowerCase(),
    );
    if (new Set(displayNames).size !== displayNames.length) {
      context.addIssue({
        code: "custom",
        message: "Actor display names must be unique.",
        path: ["actors"],
      });
    }
    const templateIds = value.actors.map((actor) => actor.templateId);
    if (new Set(templateIds).size !== templateIds.length) {
      context.addIssue({
        code: "custom",
        message: "Actor templates must be unique.",
        path: ["actors"],
      });
    }
    if (
      !value.actors.some((actor) => actor.templateId === value.leadTemplateId)
    ) {
      context.addIssue({
        code: "custom",
        message: "leadTemplateId must identify a selected actor template.",
        path: ["leadTemplateId"],
      });
    }
    const repositoryKeys =
      value.environment?.repositories.map(
        (repository) =>
          `${repository.providerId.toLowerCase()}:${repository.repositoryId.toLowerCase()}`,
      ) ?? [];
    if (new Set(repositoryKeys).size !== repositoryKeys.length) {
      context.addIssue({
        code: "custom",
        message: "Selected repositories must be unique.",
        path: ["environment", "repositories"],
      });
    }
  })
  .meta({ ref: "DashboardIssueWorkspaceCreateRequest" });

export const dashboardIssueWorkspaceReplaySchema = z.enum([
  "started",
  "attached",
  "resumed",
  "complete",
]);

export const dashboardIssueWorkspaceStateSchema = z.enum([
  "in_progress",
  "ready",
  "partial",
]);

export const dashboardIssueWorkspaceEnvironmentStateSchema = z.enum([
  "skipped",
  "pending",
  "provisioning",
  "ready",
  "failed",
]);

export const dashboardIssueWorkspaceCollectionStateSchema = z.enum([
  "skipped",
  "pending",
  "provisioning",
  "ready",
  "partial",
  "failed",
]);

export const dashboardIssueWorkspaceOpeningContextStateSchema = z.enum([
  "pending",
  "queued",
  "wake_blocked",
  "failed",
]);

const dashboardIssueWorkspaceErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  retryable: z.boolean(),
  stage: z.enum([
    "channel",
    "ticket_message",
    "environment",
    "repositories",
    "actors",
    "opening_context",
  ]),
});

const dashboardIssueWorkspaceActorItemSchema = z.object({
  displayName: z.string(),
  error: z.string().nullable(),
  instanceId: z.string().nullable(),
  state: z.enum(["pending", "provisioning", "ready", "failed"]),
  templateId: z.string(),
});

export const dashboardIssueWorkspaceCreateResponseSchema = z
  .object({
    channel: z.object({ displayName: z.string(), id: z.string() }),
    errors: z.array(dashboardIssueWorkspaceErrorSchema),
    issue: z.object({
      issueKey: z.string(),
      providerId: z.string(),
      summary: z.string().nullable(),
    }),
    operationId: z.string(),
    poll: z.object({
      environment: z.string(),
      instances: z.string(),
      repositories: z.string(),
    }),
    replay: dashboardIssueWorkspaceReplaySchema,
    stages: z.object({
      actors: z.object({
        items: z.array(dashboardIssueWorkspaceActorItemSchema),
        state: dashboardIssueWorkspaceCollectionStateSchema,
      }),
      channel: z.object({ state: z.literal("ready") }),
      environment: z.object({
        channelEnvironmentId: z.string().nullable(),
        error: z.string().nullable(),
        state: dashboardIssueWorkspaceEnvironmentStateSchema,
      }),
      openingContext: z.object({
        error: z.string().nullable(),
        mailboxEventId: z.string().nullable(),
        recipientInstanceId: z.string().nullable(),
        role: z.enum(["designated", "fallback"]).nullable(),
        state: dashboardIssueWorkspaceOpeningContextStateSchema,
        wakeBlockedReason: z.string().nullable(),
        wakeBlockedUntil: z.string().nullable(),
      }),
      repositories: z.object({
        failed: z.array(
          z.object({
            error: z.string(),
            providerId: z.string(),
            repositoryId: z.string(),
          }),
        ),
        ready: z.number().int().nonnegative(),
        requested: z.number().int().nonnegative(),
        state: dashboardIssueWorkspaceCollectionStateSchema,
      }),
      ticketMessage: z.object({
        messageId: z.string(),
        state: z.literal("ready"),
      }),
    }),
    state: dashboardIssueWorkspaceStateSchema,
  })
  .meta({ ref: "DashboardIssueWorkspaceCreateResponse" });

export const dashboardIssueWorkspaceDeleteParamSchema = z.object({
  channelId: z.string().trim().min(1),
});

export const dashboardIssueWorkspaceDeleteResponseSchema = z
  .object({
    actorsKilled: z.number().int().nonnegative(),
    environmentStatus: z.enum(["absent", "hibernated"]),
    status: z.enum(["absent", "deleted"]),
  })
  .meta({ ref: "DashboardIssueWorkspaceDeleteResponse" });

// Issue Integration Responses

export const dashboardIssueProvidersListResponseSchema = z
  .object({
    connect_url: z.string().nullable(),
    providers: z.array(dashboardIssueProviderSchema),
  })
  .meta({ ref: "DashboardIssueProvidersListResponse" });

export const dashboardIssueProviderDetailResponseSchema = z
  .object({
    credentials: z.array(dashboardIssueCredentialSchema),
    projects: z.array(dashboardIssueProjectSchema),
    provider: dashboardIssueProviderSchema,
    webhook: dashboardIssueWebhookSchema,
  })
  .meta({ ref: "DashboardIssueProviderDetailResponse" });

export const dashboardIssueProjectCreateResponseSchema = z
  .object({ project: dashboardIssueProjectSchema })
  .meta({ ref: "DashboardIssueProjectCreateResponse" });

export const dashboardIssueRemoteProjectSchema = z
  .object({
    external_key: z.string(),
    metadata: jsonObjectSchema.nullable(),
    name: z.string().nullable(),
    remote_id: z.string(),
    suggested_project_id: z.string(),
    url: z.string().nullable(),
  })
  .meta({ ref: "DashboardIssueRemoteProject" });

export const dashboardIssueStatusSchema = z
  .object({
    created_at: z.number(),
    last_synced_at: z.number(),
    name: z.string(),
    normalized_status: z.string().nullable(),
    provider_id: z.string(),
    status_category_id: z.string().nullable(),
    status_category_key: z.string().nullable(),
    status_category_name: z.string().nullable(),
    status_id: z.string(),
    updated_at: z.number(),
  })
  .meta({ ref: "DashboardIssueStatus" });

export const dashboardIssueProjectIssueTypeStatusSchema = z
  .object({
    created_at: z.number(),
    issue_type_id: z.string(),
    issue_type_name: z.string(),
    last_synced_at: z.number(),
    project_external_key: z.string(),
    provider_id: z.string(),
    sort_order: z.number(),
    status_id: z.string(),
    updated_at: z.number(),
  })
  .meta({ ref: "DashboardIssueProjectIssueTypeStatus" });

export const dashboardIssueProjectsRemoteResponseSchema = z
  .object({ projects: z.array(dashboardIssueRemoteProjectSchema) })
  .meta({ ref: "DashboardIssueProjectsRemoteResponse" });

export const dashboardIssueJiraLinkCodeResponseSchema = z
  .object({ code: z.string() })
  .meta({ ref: "DashboardIssueJiraLinkCodeResponse" });

// Source Control
export const dashboardSourceControlProviderSchema = z
  .object({
    base_url: z.string().nullable(),
    created_at: z.number(),
    default_org: z.string().nullable(),
    default_project: z.string().nullable(),
    display_name: z.string(),
    is_enabled: z.boolean(),
    metadata: jsonObjectSchema.nullable(),
    provider_id: z.string(),
    tenant_id: z.string(),
    type: z.enum(["github"]),
    updated_at: z.number(),
  })
  .meta({ ref: "DashboardSourceControlProvider" });

export const dashboardSourceControlCredentialSchema = z
  .object({
    created_at: z.number(),
    created_by: z.string().nullable(),
    credential_id: z.string(),
    expires_at: z.number().nullable(),
    last_verified_at: z.number().nullable(),
    metadata: jsonObjectSchema.nullable(),
    notes: z.string().nullable(),
    provider_id: z.string(),
    rotated_at: z.number().nullable(),
    rotated_by: z.string().nullable(),
    secret_ref: z.string(),
    status: z.enum(["active", "degraded", "revoked"]),
    updated_at: z.number(),
  })
  .meta({ ref: "DashboardSourceControlCredential" });

export const dashboardSourceControlRepositorySchema = z
  .object({
    created_at: z.number(),
    default_branch: z.string().nullable(),
    description: z.string().nullable(),
    index_freshness: z
      .object({
        affected_file_count: z.number().nullable(),
        affected_files: z.array(z.string()).nullable(),
        chunks_indexed_at: z.number().nullable(),
        chunks_indexed_commit_sha: z.string().nullable(),
        commits_behind: z.number().nullable(),
        current_head_sha: z.string().nullable(),
        profile_indexed_at: z.number().nullable(),
        profile_indexed_commit_sha: z.string().nullable(),
        state: z.enum(["OK", "STALE", "NONE"]),
      })
      .optional(),
    is_required: z.boolean(),
    language: z.string().nullable(),
    last_indexed_at: z.number().nullable(),
    metadata: jsonObjectSchema.nullable(),
    provider_id: z.string(),
    remote_slug: z.string(),
    repository_id: z.string(),
    topics: z.array(z.string()).nullable(),
    updated_at: z.number(),
  })
  .meta({ ref: "DashboardSourceControlRepository" });

export const dashboardSourceControlWebhookSchema = z
  .object({
    confirmation_expires_at: z.number().nullable(),
    confirmed_at: z.number().nullable(),
    created_at: z.number(),
    events: z.array(z.string()),
    has_secret: z.boolean(),
    metadata: jsonObjectSchema.nullable(),
    organization: z.string(),
    payload_url: z.string(),
    provider_id: z.string(),
    remote_webhook_id: z.string().nullable(),
    status: z.enum(["pending", "confirmed", "expired", "delete_failed"]),
    updated_at: z.number(),
    webhook_id: z.string(),
    webhook_type: z.enum(["github_org"]),
  })
  .nullable()
  .meta({ ref: "DashboardSourceControlWebhook" });

export const dashboardIndexingRunSchema = z
  .object({
    completed_at: z.number().nullable(),
    created_at: z.number().nullable(),
    insight_count: z.number(),
    item_counts: z.object({
      failed: z.number(),
      pending: z.number(),
      queued: z.number(),
      running: z.number(),
      succeeded: z.number(),
      total: z.number(),
    }),
    metadata: jsonObjectSchema.nullable(),
    repository_count: z.number().nullable(),
    requested_by: z.string().nullable(),
    run_id: z.string(),
    run_type: z.string(),
    scope: z.string().nullable(),
    started_at: z.number().nullable(),
    status: z.enum(["pending", "running", "completed", "partial", "failed"]),
    updated_at: z.number().nullable(),
  })
  .meta({ ref: "DashboardIndexingRun" });

// Source Control Requests

export const dashboardSourceControlProviderCreateRequestSchema = z
  .object({
    base_url: z.string().trim().optional(),
    default_org: z.string().trim().optional(),
    default_project: z.string().trim().optional(),
    display_name: z.string().trim().min(1),
    provider_id: z.string().trim().pipe(id64),
    tenant_id: z.string().trim().optional(),
    type: z.enum(["github"]),
  })
  .meta({ ref: "DashboardSourceControlProviderCreateRequest" });

export const dashboardSourceControlCredentialCreateRequestSchema = z
  .object({
    created_by: optionalString,
    credential_id: z.string().trim().pipe(id64),
    expires_at: dateInput.nullable().optional(),
    last_verified_at: dateInput.nullable().optional(),
    metadata: jsonObjectSchema.nullable().optional(),
    notes: optionalString,
    rotated_at: dateInput.nullable().optional(),
    rotated_by: optionalString,
    secret_ref: z.string().trim().min(1),
    status: scCredentialStatus,
  })
  .transform((d) => ({
    createdBy: d.created_by ?? null,
    credentialId: d.credential_id,
    expiresAt: d.expires_at ?? null,
    lastVerifiedAt: d.last_verified_at ?? null,
    metadata: d.metadata ?? null,
    notes: d.notes ?? null,
    rotatedAt: d.rotated_at ?? null,
    rotatedBy: d.rotated_by ?? null,
    secretRef: d.secret_ref,
    status: d.status,
  }))
  .meta({ ref: "DashboardSourceControlCredentialCreateRequest" });

export const dashboardSourceControlCredentialUpdateRequestSchema = z
  .object({
    expires_at: dateInput.nullable().optional(),
    last_verified_at: dateInput.nullable().optional(),
    metadata: jsonObjectSchema.nullable().optional(),
    notes: optionalString,
    rotated_at: dateInput.nullable().optional(),
    status: z.enum(["active", "degraded", "revoked"]).optional(),
  })
  .transform((d) => ({
    expiresAt: d.expires_at,
    lastVerifiedAt: d.last_verified_at,
    metadata: d.metadata,
    notes: d.notes,
    rotatedAt: d.rotated_at,
    status: d.status,
  }))
  .meta({ ref: "DashboardSourceControlCredentialUpdateRequest" });

export const dashboardSourceControlRepositoryUpsertRequestSchema = z
  .object({
    default_branch: optionalString,
    is_required: z.boolean().default(false),
    metadata: jsonObjectSchema.nullable().optional(),
    remote_slug: z.string().trim().min(1),
    repository_id: z.string().trim().pipe(id64),
  })
  .transform((d) => ({
    defaultBranch: d.default_branch,
    isRequired: d.is_required,
    metadata: d.metadata ?? null,
    remoteSlug: d.remote_slug,
    repositoryId: d.repository_id,
  }))
  .meta({ ref: "DashboardSourceControlRepositoryUpsertRequest" });

export const dashboardSourceControlGithubOrgWebhookStartRequestSchema = z
  .object({
    events: z.array(z.string()).optional(),
    organization: z.string().trim().optional(),
  })
  .meta({ ref: "DashboardSourceControlGithubOrgWebhookStartRequest" });

export const dashboardSourceControlDiscoverBranchesQuerySchema = z.object({
  repositorySlug: z.string().trim().min(1).max(256),
});

// Source Control Responses

export const dashboardSourceControlProvidersListResponseSchema = z
  .object({ providers: z.array(dashboardSourceControlProviderSchema) })
  .meta({ ref: "DashboardSourceControlProvidersListResponse" });

export const dashboardSourceControlProviderCreateResponseSchema = z
  .object({ provider: dashboardSourceControlProviderSchema })
  .meta({ ref: "DashboardSourceControlProviderCreateResponse" });

export const dashboardSourceControlProviderDetailResponseSchema = z
  .object({
    credentials: z.array(dashboardSourceControlCredentialSchema),
    provider: dashboardSourceControlProviderSchema,
    repositories: z.array(dashboardSourceControlRepositorySchema),
    webhooks: z.array(dashboardSourceControlWebhookSchema),
  })
  .meta({ ref: "DashboardSourceControlProviderDetailResponse" });

export const dashboardSourceControlRepositoriesSummaryResponseSchema = z
  .object({ repositories: z.array(dashboardSourceControlRepositorySchema) })
  .meta({ ref: "DashboardSourceControlRepositoriesSummaryResponse" });

export const dashboardSourceControlCredentialCreateResponseSchema = z
  .object({ credential: dashboardSourceControlCredentialSchema })
  .meta({ ref: "DashboardSourceControlCredentialCreateResponse" });

export const dashboardSourceControlCredentialUpdateResponseSchema = z
  .object({ credential: dashboardSourceControlCredentialSchema })
  .meta({ ref: "DashboardSourceControlCredentialUpdateResponse" });

export const dashboardSourceControlRepositoryUpsertResponseSchema = z
  .object({ repositories: z.array(dashboardSourceControlRepositorySchema) })
  .meta({ ref: "DashboardSourceControlRepositoryUpsertResponse" });

export const dashboardSourceControlDiscoveredRepositorySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    projectName: z.string().optional(),
    url: z.string(),
  })
  .meta({ ref: "DashboardSourceControlDiscoveredRepository" });

export const dashboardSourceControlBranchSchema = z
  .object({ is_default: z.boolean(), name: z.string() })
  .meta({ ref: "DashboardSourceControlBranch" });

export const dashboardSourceControlDiscoverRepositoriesResponseSchema = z
  .object({
    repositories: z.array(dashboardSourceControlDiscoveredRepositorySchema),
  })
  .meta({ ref: "DashboardSourceControlDiscoverRepositoriesResponse" });

export const dashboardSourceControlDiscoverBranchesResponseSchema = z
  .object({ branches: z.array(dashboardSourceControlBranchSchema) })
  .meta({ ref: "DashboardSourceControlDiscoverBranchesResponse" });

export const dashboardSourceControlWebhookStartResponseSchema = z
  .object({
    redirect_url: z.string().optional(),
    replaced_webhook_ids: z.array(z.string()).optional(),
    webhook: dashboardSourceControlWebhookSchema.optional(),
    webhook_secret: z.string().optional(),
  })
  .meta({ ref: "DashboardSourceControlWebhookStartResponse" });

export const dashboardSourceControlGithubAppInstallStartRequestSchema = z
  .object({ redirect_url: z.string().optional() })
  .meta({ ref: "DashboardSourceControlGithubAppInstallStartRequest" });

export const dashboardSourceControlGithubAppInstallCallbackQuerySchema = z
  .object({
    installation_id: z.string().trim().default(""),
    state: z.string().trim().default(""),
  })
  .transform(({ state, installation_id }) => ({
    installationId: Number.parseInt(installation_id, 10),
    state,
  }));

export const dashboardSourceControlGithubAppInstallStartResponseSchema = z
  .object({ redirect_url: z.string() })
  .meta({ ref: "DashboardSourceControlGithubAppInstallStartResponse" });

export const dashboardIndexingRunsListResponseSchema = z
  .object({ runs: z.array(dashboardIndexingRunSchema) })
  .meta({ ref: "DashboardIndexingRunsListResponse" });

export const dashboardIndexingRunCreateRequestSchema = z
  .object({
    metadata: jsonObjectSchema.nullable().optional(),
    mode: z.enum(REPOSITORY_INDEXING_MODE_VALUES).optional(),
    reason: z.string().max(256).nullable().optional(),
    repositories: z.array(z.string()).optional(),
    repository_modes: z
      .array(
        z.object({
          mode: z.enum(REPOSITORY_INDEXING_MODE_VALUES),
          repository_slug: z.string().trim().min(1),
        }),
      )
      .max(200)
      .optional(),
    requested_by: z.string().max(128).optional(),
    scope: z.string().max(256).nullable().optional(),
  })
  .meta({ ref: "DashboardIndexingRunCreateRequest" });

export const dashboardIndexingRunCreateResponseSchema = z
  .object({ run: dashboardIndexingRunSchema })
  .meta({ ref: "DashboardIndexingRunCreateResponse" });

export const dashboardIndexingRunFailureSchema = z.object({
  error_message: z.string().nullable(),
  repository_slug: z.string(),
  run_item_id: z.string(),
  task_id: z.string().nullable(),
});

export const dashboardIndexingRunFailedRepositoriesResponseSchema = z
  .object({
    failures: z.array(dashboardIndexingRunFailureSchema),
    repositories: z.array(z.string()),
  })
  .meta({ ref: "DashboardIndexingRunFailedRepositoriesResponse" });

const dashboardIndexingInsightFacetSchema = z.object({
  created_at: z.number().nullable(),
  facet_id: z.string(),
  facet_type: z.string(),
  facet_value: z.string(),
  insight_id: z.string(),
  normalized_value: z.string(),
  updated_at: z.number().nullable(),
});

export const dashboardIndexingInsightSchema = z.object({
  architecture_pattern: z.string().nullable(),
  category: z.string().nullable(),
  confidence_score: z.number().nullable(),
  count: z.number().nullable(),
  coverage: z.number().nullable(),
  created_at: z.number().nullable(),
  description: z.string().nullable(),
  detection_method: z.string().nullable(),
  facets: z.array(dashboardIndexingInsightFacetSchema),
  fingerprint: z.string().nullable(),
  fingerprint_key: z.string(),
  insight_id: z.string(),
  insight_type: z.string(),
  metadata: jsonObjectSchema.nullable(),
  name: z.string().nullable(),
  primary_language: z.string().nullable(),
  repo_tool_command: z.string().nullable().optional(),
  repo_tool_kind: z.enum(REPO_TOOL_KIND_VALUES).nullable().optional(),
  repo_tool_status: z.enum(REPO_TOOL_STATUS_VALUES).nullable().optional(),
  repo_tool_working_directory: z.string().nullable().optional(),
  repository_id: z.string().nullable(),
  repository_slug: z.string(),
  run_id: z.string(),
  severity: z.string().nullable(),
  source_file: z.string().nullable(),
  updated_at: z.number().nullable(),
  version: z.string().nullable(),
});

export const dashboardIndexingRunInsightsResponseSchema = z
  .object({ insights: z.array(dashboardIndexingInsightSchema) })
  .meta({ ref: "DashboardIndexingRunInsightsResponse" });

// Repository Topology

const repositoryTopologyRoleSchema = z.enum([
  "frontend",
  "backend",
  "database",
  "service",
  "library",
  "unknown",
]);

export const dashboardRepositoryRelationshipSchema = z.object({
  confidence: z.number(),
  evidence: z.string().nullable(),
  relationship_type: z.string(),
  source_repository_slug: z.string(),
  target_repository_slug: z.string(),
});

export const dashboardRepositoryTopologyNodeSchema = z.object({
  framework: z.string().nullable(),
  primary_language: z.string().nullable(),
  role: repositoryTopologyRoleSchema,
  slug: z.string(),
});

export const dashboardRepositoryTopologyResponseSchema = z
  .object({
    nodes: z.array(dashboardRepositoryTopologyNodeSchema),
    relationships: z.array(dashboardRepositoryRelationshipSchema),
  })
  .meta({ ref: "DashboardRepositoryTopologyResponse" });

// Schema Telemetry
const schemaTelemetryJsonObjectSchema = z.record(z.string(), jsonValueSchema);

export const dashboardSchemaTelemetryRowSchema = z
  .object({
    callsite: z.string(),
    completionTokens: z.number().nullable(),
    costCacheWriteUsd: z.number().nullable(),
    costCachedInputUsd: z.number().nullable(),
    costInputUsd: z.number().nullable(),
    costOutputUsd: z.number().nullable(),
    costTotalUsd: z.number().nullable(),
    costUsd: z.number().nullable(),
    expectedSchema: schemaTelemetryJsonObjectSchema.nullable(),
    hasParsedResult: z.boolean(),
    hasRawResponse: z.boolean(),
    id: z.number(),
    maxTokens: z.number().nullable(),
    model: z.string().nullable(),
    modelStage: z.string().nullable(),
    parsedResultSnippet: z.string().nullable(),
    promptSnippet: z.string().nullable(),
    promptTokens: z.number().nullable(),
    provider: z.string().nullable(),
    rawResponseSnippet: z.string().nullable(),
    responseId: z.string().nullable(),
    responseModel: z.string().nullable(),
    responseSchema: schemaTelemetryJsonObjectSchema.nullable(),
    schemaName: z.string().nullable(),
    scopeId: z.string().nullable(),
    scopeType: z.string().nullable(),
    timestamp: z.string(),
    totalTokens: z.number().nullable(),
    usageCacheWriteTokens: z.number().nullable(),
    usageCachedInputTokens: z.number().nullable(),
    usageCachedTokens: z.number().nullable(),
    usageInputTokens: z.number().nullable(),
    usageOutputTokens: z.number().nullable(),
    validationErrorCount: z.number(),
    validationStatus: z.enum(["success", "invalid", "error"]),
  })
  .meta({ ref: "DashboardSchemaTelemetryRow" });

export const dashboardSchemaTelemetryListResponseSchema = z
  .object({
    page: z.number(),
    pageSize: z.number(),
    rows: z.array(dashboardSchemaTelemetryRowSchema),
    total: z.number(),
  })
  .meta({ ref: "DashboardSchemaTelemetryListResponse" });

export const dashboardSchemaTelemetryScopeSummaryResponseSchema = z
  .object({
    costUsd: z.number(),
    count: z.number(),
    scopeId: z.string(),
    scopeType: z.string(),
    tokens: z.object({
      completionTokens: z.number(),
      promptTokens: z.number(),
      totalTokens: z.number(),
    }),
    total: z.number(),
  })
  .meta({ ref: "DashboardSchemaTelemetryScopeSummaryResponse" });

export const dashboardSchemaTelemetryExportRequestSchema = z
  .object({ ids: z.array(z.number()) })
  .meta({ ref: "DashboardSchemaTelemetryExportRequest" });

// Timings
export const dashboardTimingEntrySchema = z
  .object({
    created_at: z.number(),
    duration_ms: z.number(),
    ended_at: z.number().nullable(),
    id: z.string(),
    kind: z.enum(TASK_TIMING_EVENT_KIND_VALUES),
    name: z.string().nullable(),
    payload: jsonValueSchema,
    run_id: z.string().nullable(),
    started_at: z.number().nullable(),
    step_number: z.number().nullable(),
    stream_id: z.string().nullable(),
    task_id: z.string(),
    ticket_id: z.string().nullable(),
  })
  .meta({ ref: "DashboardTimingEntry" });

export const dashboardTimingSummaryResponseSchema = z
  .object({
    totals: z.object({
      avgDurationMs: z.number().nullable(),
      count: z.number(),
      maxDurationMs: z.number().nullable(),
      minDurationMs: z.number().nullable(),
      totalDurationMs: z.number(),
    }),
    window: z.object({
      fromTimestampMs: z.number(),
      toTimestampMs: z.number(),
    }),
  })
  .meta({ ref: "DashboardTimingSummaryResponse" });

export const dashboardTimingEntriesResponseSchema = z
  .object({
    entries: z.array(dashboardTimingEntrySchema),
    offset: z.number(),
    order: z.enum(["asc", "desc"]),
    page: z.number(),
    pageSize: z.number(),
    total: z.number(),
  })
  .meta({ ref: "DashboardTimingEntriesResponse" });

// Quota forecasting
const dashboardQuotaForecastingTotalsSchema = z.object({
  cacheWriteTokens: z.number(),
  cachedInputTokens: z.number(),
  failedRequestCount: z.number(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  reasoningTokens: z.number(),
  requestCount: z.number(),
  successfulRequestCount: z.number(),
  throttledRequestCount: z.number(),
  totalTokens: z.number(),
});

const dashboardQuotaForecastingPeaksSchema = z.object({
  maxContextWindowSize: z.number().nullable(),
  maxTokensPerCall: z.number().nullable(),
  rollingInputTokensPerMinute: z.number(),
  rollingOutputTokensPerMinute: z.number(),
  rollingRequestsPerMinute: z.number(),
  rollingTotalTokensPerMinute: z.number(),
});

const dashboardQuotaForecastingPercentilesSchema = z.object({
  p50ContextWindowSize: z.number().nullable(),
  p50TokensPerCall: z.number().nullable(),
  p95ContextWindowSize: z.number().nullable(),
  p95TokensPerCall: z.number().nullable(),
  p99ContextWindowSize: z.number().nullable(),
  p99TokensPerCall: z.number().nullable(),
});

const dashboardQuotaForecastingMinuteBucketSchema = z.object({
  end_timestamp_ms: z.number(),
  failed_request_count: z.number(),
  input_tokens: z.number(),
  output_tokens: z.number(),
  request_count: z.number(),
  rolling_input_tokens_per_minute: z.number(),
  rolling_output_tokens_per_minute: z.number(),
  rolling_requests_per_minute: z.number(),
  rolling_total_tokens_per_minute: z.number(),
  start_timestamp_ms: z.number(),
  successful_request_count: z.number(),
  throttled_request_count: z.number(),
  total_tokens: z.number(),
});

const dashboardQuotaForecastingHistogramBucketSchema = z.object({
  count: z.number(),
  end_exclusive: z.number().nullable(),
  label: z.string(),
  start_inclusive: z.number(),
});

export const dashboardQuotaForecastingEntrySchema = z
  .object({
    budget_max_input_tokens: z.number().nullable(),
    budget_percent_used: z.number().nullable(),
    budget_used_input_tokens: z.number().nullable(),
    cache_write_tokens: z.number(),
    cached_input_tokens: z.number(),
    context_window_size: z.number().nullable(),
    created_at: z.number(),
    error_code: z.string().nullable(),
    error_message: z.string().nullable(),
    error_status_code: z.number().nullable(),
    estimated_cost_usd: z.number().nullable(),
    id: z.string(),
    input_tokens: z.number(),
    model: z.string().nullable(),
    output_tokens: z.number(),
    provider: z.string(),
    reasoning_tokens: z.number(),
    run_id: z.string(),
    status: z.enum(["success", "error", "throttled"]),
    step_number: z.number(),
    stream_id: z.string().nullable(),
    timestamp_ms: z.number(),
    total_tokens: z.number(),
  })
  .meta({ ref: "DashboardQuotaForecastingEntry" });

export const dashboardQuotaForecastingSummaryResponseSchema = z
  .object({
    histograms: z.object({
      context_window_size: z.array(
        dashboardQuotaForecastingHistogramBucketSchema,
      ),
      tokens_per_call: z.array(dashboardQuotaForecastingHistogramBucketSchema),
    }),
    minute_buckets: z.array(dashboardQuotaForecastingMinuteBucketSchema),
    peaks: dashboardQuotaForecastingPeaksSchema,
    percentiles: dashboardQuotaForecastingPercentilesSchema,
    totals: dashboardQuotaForecastingTotalsSchema,
    window: z.object({
      fromTimestampMs: z.number(),
      toTimestampMs: z.number(),
    }),
  })
  .meta({ ref: "DashboardQuotaForecastingSummaryResponse" });

export const dashboardQuotaForecastingEntriesResponseSchema = z
  .object({
    entries: z.array(dashboardQuotaForecastingEntrySchema),
    offset: z.number(),
    order: z.enum(["asc", "desc"]),
    page: z.number(),
    pageSize: z.number(),
    total: z.number(),
  })
  .meta({ ref: "DashboardQuotaForecastingEntriesResponse" });

// Usage

const usageCostBreakdownSchema = z.object({
  cacheStorageUsd: z.number().optional(),
  cacheWriteUsd: z.number().optional(),
  cachedInputUsd: z.number().optional(),
  completeness: z.enum(["exact", "partial"]).optional(),
  inputUsd: z.number().optional(),
  missingPricingInputs: z.array(z.string()).optional(),
  outputUsd: z.number().optional(),
  totalUsd: z.number().optional(),
});

const usageTotalsSchema = usagePricingDimensionsSchema.extend({
  cacheWriteTokens: z.number(),
  cachedInputTokens: z.number(),
  completionTokens: z.number(),
  costBreakdown: usageCostBreakdownSchema.optional(),
  costUsd: z.number().optional(),
  promptTokens: z.number(),
  totalTokens: z.number(),
});

const usageWindowSchema = z.object({
  fromTimestampMs: z.number(),
  toTimestampMs: z.number(),
});

const usageTimeseriesBucketSchema = z.object({
  endTimestampMs: z.number(),
  startTimestampMs: z.number(),
  totals: usageTotalsSchema,
});

const usageBreakdownRowByProviderSchema = z.object({
  provider: z.string(),
  totals: usageTotalsSchema,
});

const usageBreakdownRowByStageSchema = z.object({
  stage: z.string(),
  totals: usageTotalsSchema,
});

const usageBreakdownRowByModelSchema = z.object({
  model: z.string().nullable(),
  totals: usageTotalsSchema,
});

const usageBreakdownRowByProviderModelSchema = z.object({
  model: z.string().nullable(),
  provider: z.string(),
  totals: usageTotalsSchema,
});

const usageBreakdownsSchema = z.object({
  byModel: z.array(usageBreakdownRowByModelSchema),
  byProvider: z.array(usageBreakdownRowByProviderSchema),
  byProviderModel: z.array(usageBreakdownRowByProviderModelSchema),
  byStage: z.array(usageBreakdownRowByStageSchema),
  partial: z.boolean(),
  scanned: z.number(),
  total: z.number(),
});

const usageContextEfficiencySchema = z.object({
  avoidedPromptTokens: z.number(),
  cacheHitCount: z.number(),
  coveredMatchCount: z.number(),
  exactMatchCount: z.number(),
  pricedHitCount: z.number(),
  readCount: z.number(),
  returnedTokens: z.number(),
  savedTokens: z.number(),
  savedUsdEstimate: z.number().optional(),
});

const usageTokensSchema = z.object({
  cacheWriteTokens: z.number(),
  cachedInputTokens: z.number(),
  completionTokens: z.number(),
  promptTokens: z.number(),
  totalTokens: z.number(),
});

const usageEntrySchema = usagePricingDimensionsSchema.extend({
  costBreakdown: usageCostBreakdownSchema.optional(),
  costUsd: z.number().optional(),
  entryId: z.string(),
  model: z.string().nullable(),
  provider: z.string(),
  rawUsage: jsonObjectSchema.optional(),
  stage: z.string(),
  taskId: z.string(),
  threadId: z.string().nullable(),
  ticketId: z.string().nullable(),
  timestampMs: z.number(),
  tokens: usageTokensSchema,
});

export const dashboardUsageSummaryResponseSchema = z
  .object({
    breakdowns: usageBreakdownsSchema.nullable(),
    contextEfficiency: usageContextEfficiencySchema,
    timeseries: z
      .object({
        bucketUnit: z.enum(["hour", "day"]).nullable(),
        buckets: z.array(usageTimeseriesBucketSchema),
      })
      .optional(),
    totals: usageTotalsSchema,
    window: usageWindowSchema,
  })
  .meta({ ref: "DashboardUsageSummaryResponse" });

export const dashboardUsageEntriesResponseSchema = z
  .object({
    entries: z.array(usageEntrySchema),
    offset: z.number(),
    order: z.enum(["asc", "desc"]),
    page: z.number(),
    pageSize: z.number(),
    total: z.number(),
  })
  .meta({ ref: "DashboardUsageEntriesResponse" });

const dashboardOrganizationRoleSchema = z
  .enum(["member", "admin", "owner"])
  .meta({ ref: "DashboardOrganizationRole" });

const dashboardInviteStatusSchema = z
  .enum(["pending", "accepted", "declined", "expired"])
  .meta({ ref: "DashboardInviteStatus" });

export const dashboardOrganizationSummarySchema = z
  .object({
    created_at: z.string(),
    name: z.string(),
    organization_id: z.string(),
    plan_tier: z.string(),
    slug: z.string(),
    status: z.string(),
    updated_at: z.string(),
  })
  .meta({ ref: "DashboardOrganizationSummary" });

export const dashboardOrganizationsListResponseSchema = z
  .object({ organizations: z.array(dashboardOrganizationSummarySchema) })
  .meta({ ref: "DashboardOrganizationsListResponse" });

export const dashboardOrganizationCreateRequestSchema = z
  .object({ name: z.string().trim().min(1), slug: z.string().trim().min(1) })
  .meta({ ref: "DashboardOrganizationCreateRequest" });

export const dashboardOrganizationSelectRequestSchema = z
  .object({
    organizationId: z.string().trim().min(1).optional(),
    organization_id: z.string().trim().min(1).optional(),
  })
  .refine(
    (value) =>
      typeof value.organizationId === "string" ||
      typeof value.organization_id === "string",
    { message: "organizationId is required." },
  )
  .meta({ ref: "DashboardOrganizationSelectRequest" });

export const dashboardOrganizationSelectResponseSchema = z
  .object({ ok: z.boolean(), organization_id: z.string() })
  .meta({ ref: "DashboardOrganizationSelectResponse" });

export const dashboardOrganizationMemberSchema = z
  .object({
    created_at: z.string(),
    email: z.string().nullable(),
    id: z.string(),
    name: z.string().nullable(),
    role: dashboardOrganizationRoleSchema,
    user_id: z.string(),
  })
  .meta({ ref: "DashboardOrganizationMember" });

export const dashboardOrganizationMembersListResponseSchema = z
  .object({ members: z.array(dashboardOrganizationMemberSchema) })
  .meta({ ref: "DashboardOrganizationMembersListResponse" });

export const dashboardOrganizationMemberAddRequestSchema = z
  .object({
    email: z.email(),
    role: dashboardOrganizationRoleSchema.default("member"),
  })
  .meta({ ref: "DashboardOrganizationMemberAddRequest" });

export const dashboardOrganizationMemberUpdateRequestSchema = z
  .object({ role: dashboardOrganizationRoleSchema })
  .meta({ ref: "DashboardOrganizationMemberUpdateRequest" });

export const dashboardOrganizationInviteSchema = z
  .object({
    created_at: z.string(),
    email: z.string(),
    expires_at: z.string(),
    id: z.string(),
    invited_by_user_id: z.string(),
    role: dashboardOrganizationRoleSchema,
    status: dashboardInviteStatusSchema,
  })
  .meta({ ref: "DashboardOrganizationInvite" });

export const dashboardOrganizationInvitesListResponseSchema = z
  .object({ invites: z.array(dashboardOrganizationInviteSchema) })
  .meta({ ref: "DashboardOrganizationInvitesListResponse" });

export const dashboardOrganizationInviteInvalidateResponseSchema = z
  .object({
    invite_id: z.string(),
    ok: z.boolean(),
    status: dashboardInviteStatusSchema,
  })
  .meta({ ref: "DashboardOrganizationInviteInvalidateResponse" });

export const dashboardPendingInviteSchema = z
  .object({
    created_at: z.string(),
    expires_at: z.string(),
    id: z.string(),
    inviter_email: z.string().nullable(),
    inviter_name: z.string().nullable(),
    organization_name: z.string(),
    organization_slug: z.string().nullable(),
    role: dashboardOrganizationRoleSchema,
    status: dashboardInviteStatusSchema,
    token: z.string(),
  })
  .meta({ ref: "DashboardPendingInvite" });

export const dashboardPendingInvitesListResponseSchema = z
  .object({ invites: z.array(dashboardPendingInviteSchema) })
  .meta({ ref: "DashboardPendingInvitesListResponse" });

export const dashboardInviteAcceptResponseSchema = z
  .object({
    already_member: z.boolean().optional(),
    ok: z.boolean(),
    organization_id: z.string().optional(),
  })
  .meta({ ref: "DashboardInviteAcceptResponse" });

export const dashboardInviteDeclineResponseSchema = z
  .object({ ok: z.boolean() })
  .meta({ ref: "DashboardInviteDeclineResponse" });

export const dashboardOrganizationApiKeySchema = z
  .object({
    created_at: z.string(),
    has_key: z.boolean(),
    label: z.string().nullable(),
    provider: z.string(),
    provider_key_id: z.string().nullable(),
    updated_at: z.string(),
  })
  .meta({ ref: "DashboardOrganizationApiKey" });

export const dashboardOrganizationApiKeysListResponseSchema = z
  .object({ items: z.array(dashboardOrganizationApiKeySchema) })
  .meta({ ref: "DashboardOrganizationApiKeysListResponse" });

export const dashboardMcpResourceQuerySchema = z.object({
  resource: mcpResourceSchema.default("arti"),
});

export const dashboardMcpTokenCreateRequestSchema =
  mcpResourceScopeSelectionSchema
    .safeExtend({
      label: z.string().trim().min(1).max(128).nullable().optional(),
    })
    .meta({ ref: "DashboardMcpTokenCreateRequest" });

export const dashboardMcpTokenSchema = z
  .object({
    created_at: z.string(),
    expires_at: z.string(),
    label: z.string(),
    last_used_at: z.string().nullable(),
    resource: mcpResourceSchema,
    scopes: z.array(z.string()),
    token_id: z.string(),
  })
  .meta({ ref: "DashboardMcpToken" });

export const dashboardMcpTokenIdParamSchema = z.object({
  tokenId: z.string().trim().min(1),
});

export const dashboardMcpTokensListResponseSchema = z
  .object({ enabled: z.boolean(), items: z.array(dashboardMcpTokenSchema) })
  .meta({ ref: "DashboardMcpTokensListResponse" });

export const dashboardMcpTokenCreateResponseSchema = z
  .object({
    ...dashboardMcpTokenSchema.shape,
    organization_id: z.string(),
    token: z.string(),
    user_id: z.string(),
  })
  .meta({ ref: "DashboardMcpTokenCreateResponse" });

export const dashboardMcpTokenRevokeResponseSchema = z
  .object({ ok: z.boolean(), token_id: z.string() })
  .meta({ ref: "DashboardMcpTokenRevokeResponse" });

export const dashboardMcpGrantSchema = z
  .object({
    client_name: z.string(),
    created_at: z.string(),
    grant_id: z.string(),
    last_used_at: z.string().nullable(),
    oauth_client_id: z.string(),
    redirect_uri: z.string().url(),
    resource: z.string().url(),
    scopes: z.array(z.string()),
  })
  .meta({ ref: "DashboardMcpGrant" });

export const dashboardMcpGrantIdParamSchema = z.object({
  grantId: z.string().trim().min(1),
});

export const dashboardMcpGrantsListResponseSchema = z
  .object({ items: z.array(dashboardMcpGrantSchema) })
  .meta({ ref: "DashboardMcpGrantsListResponse" });

export const dashboardMcpGrantRevokeResponseSchema = z
  .object({ grant_id: z.string(), ok: z.boolean() })
  .meta({ ref: "DashboardMcpGrantRevokeResponse" });

export const dashboardMcpOAuthAuthorizationServerMetadataSchema = z
  .object({
    authorization_endpoint: z.string().url(),
    code_challenge_methods_supported: z.array(z.string()).optional(),
    grant_types_supported: z.array(z.string()).optional(),
    issuer: z.string().url(),
    registration_endpoint: z.string().url().optional(),
    response_modes_supported: z.array(z.string()).optional(),
    response_types_supported: z.array(z.string()),
    scopes_supported: z.array(z.string()).optional(),
    token_endpoint: z.string().url(),
    token_endpoint_auth_methods_supported: z.array(z.string()).optional(),
  })
  .meta({ ref: "DashboardMcpOAuthAuthorizationServerMetadata" });

export const dashboardMcpOAuthProtectedResourceMetadataSchema = z
  .object({
    authorization_servers: z.array(z.string().url()).optional(),
    bearer_methods_supported: z.array(z.string()).optional(),
    resource: z.string().url(),
    resource_name: z.string().optional(),
    scopes_supported: z.array(z.string()).optional(),
  })
  .meta({ ref: "DashboardMcpOAuthProtectedResourceMetadata" });

export const dashboardMcpOAuthAuthorizeQuerySchema = z.object({
  client_id: z.string().trim().min(1),
  code_challenge: z.string().trim().min(1),
  code_challenge_method: z.literal("S256"),
  redirect_uri: z.string().url(),
  resource: z.string().url().optional(),
  response_type: z.literal("code"),
  scope: z.string().trim().min(1).optional(),
  state: z.string().trim().min(1).optional(),
});

export const dashboardMcpOAuthAuthorizeDecisionFormSchema = z.object({
  client_id: z.string().trim().min(1),
  code_challenge: z.string().trim().min(1),
  code_challenge_method: z.literal("S256"),
  decision: z.enum(["allow", "deny"]),
  redirect_uri: z.string().url(),
  resource: z.string().url().optional(),
  response_type: z.literal("code"),
  scope: z.string().trim().min(1).optional(),
  state: z.string().trim().min(1).optional(),
});

export const dashboardMcpOAuthTokenFormSchema = z.object({
  client_id: z.string().trim().min(1).optional(),
  client_secret: z.string().trim().min(1).optional(),
  code: z.string().trim().min(1).optional(),
  code_verifier: z.string().trim().min(1).optional(),
  grant_type: z.enum(["authorization_code", "refresh_token"]),
  redirect_uri: z.string().url().optional(),
  refresh_token: z.string().trim().min(1).optional(),
  resource: z.string().url().optional(),
  scope: z.string().trim().min(1).optional(),
});

export const dashboardMcpOAuthDynamicClientRegistrationRequestSchema = z
  .object({
    client_name: z.string().trim().min(1).optional(),
    grant_types: z.array(z.string().trim().min(1)).optional(),
    redirect_uris: z.array(z.string().url()).optional(),
    response_types: z.array(z.string().trim().min(1)).optional(),
    token_endpoint_auth_method: z.string().trim().min(1).optional(),
  })
  .meta({ ref: "DashboardMcpOAuthDynamicClientRegistrationRequest" });

export const dashboardMcpOAuthTokenResponseSchema = z
  .object({
    access_token: z.string(),
    expires_in: z.number().int().positive(),
    refresh_token: z.string(),
    scope: z.string(),
    token_type: z.literal("Bearer"),
  })
  .meta({ ref: "DashboardMcpOAuthTokenResponse" });

export const dashboardMcpOAuthDynamicClientRegistrationResponseSchema = z
  .object({
    client_id: z.string(),
    client_id_issued_at: z.number().int().nonnegative(),
    client_name: z.string().optional(),
    client_secret: z.string().optional(),
    client_secret_expires_at: z.number().int().nonnegative().optional(),
    grant_types: z.array(z.string()),
    redirect_uris: z.array(z.string().url()),
    response_types: z.array(z.string()),
    scope: z.string(),
    token_endpoint_auth_method: z.string(),
  })
  .meta({ ref: "DashboardMcpOAuthDynamicClientRegistrationResponse" });

export const dashboardBillingTopUpRequestSchema = z
  .object({ amountCents: z.number().int().positive() })
  .meta({ ref: "DashboardBillingTopUpRequest" });

export const dashboardBillingPortalRequestSchema = z
  .object({ returnUrl: z.string().trim().min(1) })
  .meta({ ref: "DashboardBillingPortalRequest" });

export const dashboardBillingSetupResponseSchema = z
  .object({ alreadySetUp: z.boolean(), customerId: z.string() })
  .meta({ ref: "DashboardBillingSetupResponse" });

export const dashboardBillingTopUpResponseSchema = z
  .object({ sessionUrl: z.string() })
  .meta({ ref: "DashboardBillingTopUpResponse" });

export const dashboardBillingPortalResponseSchema = z
  .object({ portalUrl: z.string() })
  .meta({ ref: "DashboardBillingPortalResponse" });

export const dashboardBillingBalanceResponseSchema = z
  .object({
    balanceUsd: z.number(),
    hasBillingProfile: z.boolean(),
    hasPaymentMethod: z.boolean().optional(),
  })
  .meta({ ref: "DashboardBillingBalanceResponse" });

export const dashboardCreditTransactionSchema = z
  .object({
    amountUsd: z.number(),
    balanceAfterUsd: z.number(),
    createdAt: z.string(),
    description: z.string().nullable(),
    id: z.string(),
    taskId: z.string().nullable(),
    type: z.enum(["topup", "deduction", "grant", "expiry"]),
  })
  .meta({ ref: "DashboardCreditTransaction" });

export const dashboardBillingTransactionsQuerySchema = z.object({
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
  type: z.enum(["topup", "deduction", "grant", "expiry"]).optional(),
});

export const dashboardBillingTransactionsResponseSchema = z
  .object({
    items: z.array(dashboardCreditTransactionSchema),
    total: z.number(),
  })
  .meta({ ref: "DashboardBillingTransactionsResponse" });

export const dashboardAdminOrganizationSchema = z
  .object({
    created_at: z.string(),
    credit_balance_usd: z.number(),
    member_count: z.number(),
    name: z.string(),
    organization_id: z.string(),
    plan_tier: z.string(),
    slug: z.string(),
    status: z.string(),
  })
  .meta({ ref: "DashboardAdminOrganization" });

export const dashboardAdminOrganizationsListResponseSchema = z
  .object({ organizations: z.array(dashboardAdminOrganizationSchema) })
  .meta({ ref: "DashboardAdminOrganizationsListResponse" });

export const dashboardAdminGrantCreditsRequestSchema = z
  .object({
    amountUsd: z.number().positive(),
    description: z.string().trim().optional(),
    expiresAt: z.string().trim().optional(),
  })
  .meta({ ref: "DashboardAdminGrantCreditsRequest" });

export const dashboardAdminGrantCreditsResponseSchema = z
  .object({
    amountUsd: z.number(),
    balanceAfterUsd: z.number(),
    expiresAt: z.string().nullable(),
    id: z.string(),
  })
  .meta({ ref: "DashboardAdminGrantCreditsResponse" });

export const dashboardFeatureFlagNameSchema = z.enum(FEATURE_FLAG_NAMES);

export const dashboardAdminFeatureFlagDefinitionSchema = z
  .object({
    description: z.string(),
    flag_name: dashboardFeatureFlagNameSchema,
    label: z.string(),
    org_overridable: z.boolean(),
    snapshot_default_value: z.boolean(),
  })
  .meta({ ref: "DashboardAdminFeatureFlagDefinition" });

export const dashboardAdminFeatureFlagOverrideSchema = z
  .object({
    flag_name: dashboardFeatureFlagNameSchema,
    organization_id: z.string(),
    reason: z.string().nullable(),
    set_at: z.string(),
    set_by_user_id: z.string().nullable(),
    value: z.boolean(),
  })
  .meta({ ref: "DashboardAdminFeatureFlagOverride" });

export const dashboardAdminFeatureFlagEffectiveValueSchema = z
  .object({
    effective_value: z.boolean(),
    flag_name: dashboardFeatureFlagNameSchema,
    organization_id: z.string(),
    override_value: z.boolean().nullable(),
    snapshot_default_value: z.boolean(),
  })
  .meta({ ref: "DashboardAdminFeatureFlagEffectiveValue" });

export const dashboardAdminFeatureFlagsListResponseSchema = z
  .object({
    effective_values: z.array(dashboardAdminFeatureFlagEffectiveValueSchema),
    flags: z.array(dashboardAdminFeatureFlagDefinitionSchema),
    organizations: z.array(dashboardAdminOrganizationSchema),
    overrides: z.array(dashboardAdminFeatureFlagOverrideSchema),
  })
  .meta({ ref: "DashboardAdminFeatureFlagsListResponse" });

export const dashboardAdminFeatureFlagParamSchema = z.object({
  flag: dashboardFeatureFlagNameSchema,
  organizationId: z.string().min(1),
});

export const dashboardAdminFeatureFlagOverrideRequestSchema = z
  .object({
    reason: z.string().trim().max(1024).optional(),
    value: z.boolean().nullable(),
  })
  .meta({ ref: "DashboardAdminFeatureFlagOverrideRequest" });

export const dashboardAdminFeatureFlagOverrideResponseSchema = z
  .object({
    changed: z.boolean(),
    effective_value: z.boolean(),
    flag_name: dashboardFeatureFlagNameSchema,
    organization_id: z.string(),
    override: dashboardAdminFeatureFlagOverrideSchema.nullable(),
  })
  .meta({ ref: "DashboardAdminFeatureFlagOverrideResponse" });

// --- host fencing ----------------------------------------------------------

export const dashboardAdminHostBindingSchema = z
  .object({
    enrolled_at: z.string(),
    enrolled_by_user_id: z.string().nullable(),
    enrolled_reason: z.string(),
    evidence_count: z.number().int().nonnegative(),
    host_identity: z.string(),
    host_incarnation_id: z.string(),
    id: z.string(),
    node_id: z.string(),
    provider: z.string(),
    provider_account_id: z.string(),
    provider_region: z.string(),
    /** Null while active. Non-null is permanent: retirement never reverses. */
    retired_at: z.string().nullable(),
  })
  .meta({ ref: "DashboardAdminHostBinding" });

export const dashboardAdminHostFencingListResponseSchema = z
  .object({
    bindings: z.array(dashboardAdminHostBindingSchema),
    fencing_enabled: z.boolean(),
  })
  .meta({ ref: "DashboardAdminHostFencingListResponse" });

export const dashboardAdminHostFenceTargetSchema = z
  .object({
    environment_id: z.string(),
    organization_id: z.string(),
    owner_boot_id: z.string().nullable(),
    owner_epoch: z.number().int(),
    owner_lease_epoch: z.number().int(),
    owner_node_id: z.string(),
    status: z.string(),
  })
  .meta({ ref: "DashboardAdminHostFenceTarget" });

export const dashboardAdminHostFenceEvidenceSchema = z
  .object({
    actor_email_snapshot: z.string().nullable(),
    actor_user_id: z.string().nullable(),
    asserted_at: z.string(),
    asserted_state: z.string(),
    id: z.string(),
    reason: z.string(),
    request_id: z.string(),
    source: z.string(),
    target_count: z.number().int().nonnegative(),
    valid_until: z.string(),
  })
  .meta({ ref: "DashboardAdminHostFenceEvidence" });

export const dashboardAdminHostFencingDetailResponseSchema = z
  .object({
    binding: dashboardAdminHostBindingSchema,
    /** Server-computed over the COMPLETE target set, never over a page. */
    confirmation_digest: z.string(),
    /** Exact phrase the operator must retype, byte for byte. */
    confirmation_phrase: z.string(),
    evidence: z.array(dashboardAdminHostFenceEvidenceSchema),
    fencing_enabled: z.boolean(),
    node_last_online_at: z.string().nullable(),
    node_offline_ms: z.number().int().nullable(),
    node_status: z.string().nullable(),
    /** Whether the offline floor is met right now. */
    offline_precondition_met: z.boolean(),
    targets: z.array(dashboardAdminHostFenceTargetSchema),
  })
  .meta({ ref: "DashboardAdminHostFencingDetailResponse" });

export const dashboardAdminHostFencingEnrollRequestSchema = z
  .object({
    host_identity: z.string().trim().min(1).max(128),
    node_id_prefix: z.string().trim().max(24).optional(),
    provider_account_id: z.string().trim().min(1).max(32),
    provider_region: z.string().trim().min(1).max(32),
    reason: z.string().trim().min(1).max(1024),
  })
  .meta({ ref: "DashboardAdminHostFencingEnrollRequest" });

export const dashboardAdminHostFencingEnrollResponseSchema = z
  .object({
    binding: dashboardAdminHostBindingSchema,
    /**
     * The node's configuration, returned ONCE at enrollment. The node id and
     * incarnation are generated here; a node that supplies its own is not
     * enrolled and cannot be admitted.
     */
    node_configuration: z.object({
      ARTI_NODE_ID: z.string(),
      ARTI_SANDBOX_HOST_ACCOUNT_ID: z.string(),
      ARTI_SANDBOX_HOST_IDENTITY: z.string(),
      ARTI_SANDBOX_HOST_INCARNATION_ID: z.string(),
      ARTI_SANDBOX_HOST_PROVIDER: z.string(),
      ARTI_SANDBOX_HOST_REGION: z.string(),
      AWS_EC2_METADATA_DISABLED: z.string(),
    }),
  })
  .meta({ ref: "DashboardAdminHostFencingEnrollResponse" });

export const dashboardAdminHostFencingParamSchema = z.object({
  bindingId: z.string().min(1),
});

export const dashboardAdminHostFencingAttestRequestSchema = z
  .object({
    /** Every box must be ticked; each names a distinct way to be wrong. */
    acknowledgements: z.object({
      auto_start_disabled: z.boolean(),
      not_relying_on_reachability: z.boolean(),
      used_provider_control_plane: z.boolean(),
    }),
    asserted_state: z.enum(["destroyed", "powered_off_retired"]),
    confirmation_digest: z.string().min(1),
    confirmation_phrase: z.string().min(1),
    /**
     * The last-online observation the page displayed, compared exactly inside
     * the evidence transaction.
     *
     * "Offline for at least five minutes" is not the thing the operator
     * confirmed — they confirmed a specific outage. A host that heartbeats and
     * dies again satisfies the predicate while invalidating the observation,
     * so the observation is what travels with the request. Null means the page
     * showed no marker, which fails closed.
     */
    expected_last_online_at: z.string().nullable(),
    reason: z.string().trim().min(1).max(1024),
    /** Idempotency key: a retried POST returns the first call's evidence. */
    request_id: z.string().trim().min(8).max(64),
  })
  .meta({ ref: "DashboardAdminHostFencingAttestRequest" });

export const dashboardAdminHostFencingAttestResponseSchema = z
  .object({
    evidence: dashboardAdminHostFenceEvidenceSchema,
    status: z.enum(["created", "duplicate_request"]),
    targets: z.array(dashboardAdminHostFenceTargetSchema),
  })
  .meta({ ref: "DashboardAdminHostFencingAttestResponse" });

export const dashboardAdminAgentModeBudgetDefaultSchema = z
  .object({
    max_input_token_budget: z.number().nullable(),
    mode: z.string(),
    phase: z.string(),
  })
  .meta({ ref: "DashboardAdminAgentModeBudgetDefault" });

export const dashboardAdminAgentModeBudgetsListResponseSchema = z
  .object({ defaults: z.array(dashboardAdminAgentModeBudgetDefaultSchema) })
  .meta({ ref: "DashboardAdminAgentModeBudgetsListResponse" });

export const dashboardAdminAgentModeBudgetsReseedRequestSchema = z
  .object({
    mode: z.string().trim().optional(),
    organization_ids: z.array(z.string().trim().min(1)).optional(),
  })
  .meta({ ref: "DashboardAdminAgentModeBudgetsReseedRequest" });

export const dashboardAdminAgentModeBudgetsReseedResponseSchema = z
  .object({
    inserted: z.number(),
    mode: z.string().nullable(),
    targeted_mode_count: z.number(),
    targeted_organization_count: z.number(),
    updated: z.number(),
  })
  .meta({ ref: "DashboardAdminAgentModeBudgetsReseedResponse" });

export const dashboardAdminTeamsRegistrationSchema = z
  .object({
    app_type: z.literal("SingleTenant"),
    bot_app_id: z.string(),
    client_secret_set: z.boolean(),
    environment_key: z.string(),
    home_tenant_id: z.string(),
    status: z.string(),
    validated_at: z.string().nullable(),
  })
  .meta({ ref: "DashboardAdminTeamsRegistration" });

export const dashboardAdminTeamsStatusSchema = z
  .object({
    active_bindings_count: z.number().nullable(),
    bot_status: z.string(),
    last_webhook_activity_at: z.string().nullable(),
  })
  .meta({ ref: "DashboardAdminTeamsStatus" });

export const dashboardAdminTeamsSetupResponseSchema = z
  .object({
    detected_dashboard_url: z.string().nullable(),
    endpoints: z.object({
      dashboard_public_base_url: z.string().nullable(),
      orchestrator_public_base_url: z.string().nullable(),
    }),
    registration: dashboardAdminTeamsRegistrationSchema.nullable(),
    status: dashboardAdminTeamsStatusSchema,
  })
  .meta({ ref: "DashboardAdminTeamsSetupResponse" });

export const dashboardAdminTeamsOrganizationQuerySchema = z.object({
  organization_id: z.string().trim().min(1),
});

export const dashboardAdminTeamsOrganizationBodySchema = z.object({
  organization_id: z.string().trim().min(1),
});

export const dashboardAdminTeamsSetupRequestSchema = z
  .object({
    appType: z.literal("SingleTenant").default("SingleTenant"),
    botAppId: z.string().trim().min(1),
    clientSecret: z.string().optional(),
    dashboardPublicBaseUrl: z.string().trim().url(),
    environmentKey: z.string().trim().min(1).default("default"),
    homeTenantId: z.string().trim().min(1),
    orchestratorPublicBaseUrl: z.string().trim().url(),
    organization_id: z.string().trim().min(1),
  })
  .meta({ ref: "DashboardAdminTeamsSetupRequest" });

export const dashboardAdminTeamsValidateResponseSchema = z
  .object({
    app_type: z.string().optional(),
    bot_app_id: z.string().optional(),
    error: z.string().optional(),
    ok: z.boolean(),
  })
  .meta({ ref: "DashboardAdminTeamsValidateResponse" });

export const dashboardAdminTeamsSetupSaveResponseSchema = z
  .object({ registration: dashboardAdminTeamsRegistrationSchema })
  .meta({ ref: "DashboardAdminTeamsSetupSaveResponse" });

export const dashboardAdminTeamsManifestZipResponseSchema = z
  .string()
  .meta({ ref: "DashboardAdminTeamsManifestZipResponse" });

export const dashboardAdminTeamsArchivedChannelsQuerySchema = z.object({
  limit: z.coerce.number().int().positive().max(200).optional(),
  organization_id: z.string().trim().min(1),
});

export const dashboardAdminTeamsArchivedChannelSchema = z
  .object({
    actor_display_name: z.string().nullable(),
    actor_instance_id: z.string().nullable(),
    archived_at: z.string(),
    archived_reason: z.string().nullable(),
    bound_by_user_id: z.string().nullable(),
    bound_via: z.string().nullable(),
    channel_display_name: z.string(),
    channel_id: z.string(),
    conversation_id: z.string().nullable(),
    conversation_label: z.string().nullable(),
    detached_at: z.string().nullable(),
    detached_by_user_id: z.string().nullable(),
    detached_via: z.string().nullable(),
    organization_id: z.string(),
  })
  .meta({ ref: "DashboardAdminTeamsArchivedChannel" });

export const dashboardAdminTeamsArchivedChannelsResponseSchema = z
  .object({ rows: z.array(dashboardAdminTeamsArchivedChannelSchema) })
  .meta({ ref: "DashboardAdminTeamsArchivedChannelsResponse" });

export const dashboardUserTeamsIntegrationStatusSchema = z
  .object({
    active_bindings_count: z.number().int().nonnegative(),
    active_installations_count: z.number().int().nonnegative(),
    archived_channels_count: z.number().int().nonnegative(),
    /** Whether this deployment has an active Teams bot registration. Installation
     *  rows alone cannot answer it: they survive a bot being disabled, and a
     *  deployment can hold rows minted by an entirely different bot. Clients that
     *  offer bind/unbind must gate on this, not on the counts. */
    bot_ready: z.boolean(),
    is_admin: z.boolean(),
    status: z.enum(["active", "inactive"]),
  })
  .meta({ ref: "DashboardUserTeamsIntegrationStatus" });

export const dashboardAdminTeamsOutboxStatusSchema = z.enum([
  "queued",
  "retrying",
  "failed_transient",
  "failed_permanent",
  "blocked",
  "expired",
  "posted",
  "skipped",
  "cancelled",
]);

export const dashboardAdminTeamsOutboxMessageKindSchema = z.enum([
  "message",
  "membership",
  "goodbye",
  "system",
]);

export const dashboardAdminTeamsOutboxQuerySchema = z.object({
  conversation: z.string().trim().optional(),
  kind: dashboardAdminTeamsOutboxMessageKindSchema.optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
  organization_id: z.string().trim().min(1),
  status: dashboardAdminTeamsOutboxStatusSchema.optional(),
});

export const dashboardAdminTeamsOutboxParamSchema = z.object({
  id: z.string().trim().min(1),
});

export const dashboardAdminTeamsOutboxRowSchema = z
  .object({
    attempts: z.number().int().nonnegative(),
    binding_id: z.string().nullable(),
    content_preview: z.string(),
    conversation_id: z.string().nullable(),
    conversation_label: z.string().nullable(),
    created_at: z.string(),
    id: z.string(),
    last_error: z.string().nullable(),
    lease_expires_at: z.string().nullable(),
    lease_owner: z.string().nullable(),
    message_kind: dashboardAdminTeamsOutboxMessageKindSchema,
    posted_external_id: z.string().nullable(),
    status: dashboardAdminTeamsOutboxStatusSchema,
    updated_at: z.string(),
  })
  .meta({ ref: "DashboardAdminTeamsOutboxRow" });

export const dashboardAdminTeamsOutboxCountSchema = z
  .object({
    count: z.number().int().nonnegative(),
    message_kind: z.string(),
    status: dashboardAdminTeamsOutboxStatusSchema,
  })
  .meta({ ref: "DashboardAdminTeamsOutboxCount" });

export const dashboardAdminTeamsOutboxListResponseSchema = z
  .object({
    counts: z.array(dashboardAdminTeamsOutboxCountSchema),
    rows: z.array(dashboardAdminTeamsOutboxRowSchema),
  })
  .meta({ ref: "DashboardAdminTeamsOutboxListResponse" });

export const dashboardAdminTeamsOutboxActionResponseSchema = z
  .object({
    row: dashboardAdminTeamsOutboxRowSchema.nullable(),
    status: z.string(),
  })
  .meta({ ref: "DashboardAdminTeamsOutboxActionResponse" });

export const dashboardKeystoreEntrySchema = z
  .object({
    assigned_at: z.string().nullable(),
    assigned_org_id: z.string().nullable(),
    assigned_org_name: z.string().nullable(),
    bedrock_inference_profile_arn: z.string().nullable(),
    created_at: z.string(),
    entry_id: z.string(),
    label: z.string().nullable(),
    provider: z.string(),
    provider_key_id: z.string().nullable(),
    status: z.enum(["available", "assigned"]),
    updated_at: z.string(),
  })
  .meta({ ref: "DashboardKeystoreEntry" });

export const dashboardAdminKeystoreListQuerySchema = z.object({
  provider: z.string().trim().optional(),
  status: z.enum(["available", "assigned"]).optional(),
});

export const dashboardAdminKeystoreListResponseSchema = z
  .object({ entries: z.array(dashboardKeystoreEntrySchema) })
  .meta({ ref: "DashboardAdminKeystoreListResponse" });

export const dashboardAdminKeystoreCreateRequestSchema = z
  .object({
    key: z.string().trim().min(1),
    label: z.string().trim().optional(),
    provider: z.string().trim().min(1),
    providerKeyId: z.string().trim().optional(),
  })
  .meta({ ref: "DashboardAdminKeystoreCreateRequest" });

export const dashboardAdminKeystoreAssignRequestSchema = z
  .object({ organizationId: z.string().trim().min(1) })
  .meta({ ref: "DashboardAdminKeystoreAssignRequest" });

export const dashboardAdminKeystoreAssignResponseSchema = z
  .object({
    assigned_org_id: z.string(),
    assigned_org_name: z.string(),
    config_id: z.string(),
    entry_id: z.string(),
    status: z.enum(["available", "assigned"]),
  })
  .meta({ ref: "DashboardAdminKeystoreAssignResponse" });

export const dashboardAdminKeystoreUnassignResponseSchema = z
  .object({ entry_id: z.string(), status: z.enum(["available", "assigned"]) })
  .meta({ ref: "DashboardAdminKeystoreUnassignResponse" });

export const dashboardAdminKeystoreDeleteResponseSchema = z
  .object({ deleted: z.boolean() })
  .meta({ ref: "DashboardAdminKeystoreDeleteResponse" });

export const dashboardAdminKeystoreSpendingQuerySchema = z.object({
  end: z.string().optional(),
  start: z.string().optional(),
});

export const dashboardAdminKeystoreSpendingEntrySchema = z
  .object({
    amount_usd: z.number().nullable(),
    entry_id: z.string(),
    error: z.string().optional(),
  })
  .meta({ ref: "DashboardAdminKeystoreSpendingEntry" });

export const dashboardAdminKeystoreSpendingResponseSchema = z
  .object({
    entries: z.array(dashboardAdminKeystoreSpendingEntrySchema),
    period: z.object({ end: z.string(), start: z.string() }),
    warnings: z.array(z.string()),
  })
  .meta({ ref: "DashboardAdminKeystoreSpendingResponse" });

export const dashboardAdminInvalidateSessionsResponseSchema = z
  .object({ invalidated: z.boolean() })
  .meta({ ref: "DashboardAdminInvalidateSessionsResponse" });

export const dashboardAdminUserOrgSchema = z.object({
  name: z.string(),
  organization_id: z.string(),
  role: z.string(),
});

export const dashboardAdminUserSchema = z
  .object({
    active_sessions: z.number(),
    created_at: z.string(),
    email: z.string(),
    global_role: z.string(),
    name: z.string().nullable(),
    organizations: z.array(dashboardAdminUserOrgSchema),
    user_id: z.string(),
  })
  .meta({ ref: "DashboardAdminUser" });

export const dashboardAdminUsersListResponseSchema = z
  .object({ users: z.array(dashboardAdminUserSchema) })
  .meta({ ref: "DashboardAdminUsersListResponse" });

export const dashboardStripeWebhookResponseSchema = z
  .object({ received: z.boolean() })
  .meta({ ref: "DashboardStripeWebhookResponse" });

export const publicPlatformStatsResponseSchema = z
  .object({
    generated_at: z.number(),
    providers_active: z.number(),
    success_rate: z.number(),
    tasks_executed: z.number(),
    tokens_processed: z.number(),
  })
  .meta({ ref: "PublicPlatformStatsResponse" });

// Actor Instances (dashboard /user/instances)

const dashboardActorChannelSchema = z.object({
  displayName: z.string(),
  id: z.string(),
  provenance: z.enum(["issue_workspace", "manual", "teams_bind"]),
});

export const dashboardChannelsListQuerySchema = z.object({
  channel_ids: z
    .string()
    .optional()
    .transform((value) =>
      value === undefined
        ? undefined
        : value.split(",").map((channelId) => channelId.trim()),
    )
    .pipe(z.array(z.string().min(1).max(64)).min(1).max(500).optional()),
  include_deleted: z
    .enum(["true", "false"])
    .optional()
    .transform((value) => value === "true"),
  include_dormant: z
    .enum(["true", "false"])
    .optional()
    .transform((value) => value === "true"),
  include_private: z
    .enum(["true", "false"])
    .optional()
    .transform((value) => value === "true"),
});

const dashboardUserChannelSummarySchema = z.object({
  activeTeamsBindingId: z.string().nullable(),
  activeTeamsConversationId: z.string().nullable(),
  activeTeamsConversationLabel: z.string().nullable(),
  addressedCount: z.number(),
  archivedAt: z.string().datetime().nullable(),
  archivedReason: z.string().nullable(),
  claimedCount: z.number(),
  createdAt: z.string(),
  deletedAt: z.string().datetime().nullable(),
  deletedReason: z.string().nullable(),
  deletionStartedAt: z.string().datetime().nullable(),
  displayName: z.string(),
  environmentStatus: z.string().nullable(),
  hasDetachedTeamsBinding: z.boolean(),
  id: z.string(),
  kind: z.enum(["private", "shared"]),
  lastActivityAt: z.string().nullable(),
  lastWakeBatchId: z.string().nullable(),
  memberAvatarSeeds: z.array(z.string()),
  memberCount: z.number(),
  memberlessSince: z.string().datetime().nullable(),
  oldestUnclaimedAge: z.number().nullable(),
  organizationId: z.string(),
  ownerActorId: z.string().nullable(),
  provenance: z.enum(["issue_workspace", "manual", "teams_bind"]),
  provenanceRef: z.string().nullable(),
  teamsBindingChannelKind: z.enum(["teams_only", "shared_bound"]).nullable(),
});

const dashboardChannelEnvironmentReservationAccountingSchema = z.object({
  activeRamMB: z.number(),
  totalCpuSeconds: z.number(),
  totalRamHours: z.number(),
});

const dashboardChannelEnvironmentSpawnAccountingSchema = z.object({
  maxMemMaxBytes: z.number(),
  networkBytes: z.number(),
  totalCpuMs: z.number(),
  totalSpawns: z.number(),
});

// The live resource reservation's held breakdown — reserved compute plus
// disjoint storage segments that sum to totalMb. `computeReleasedAt` is the
// "freed at rest" marker: null while the env is live (compute reserved), set
// once hibernated (compute released, storage retained). Null when the env holds
// no reservation.
const dashboardChannelEnvironmentReservationHeldSchema = z.object({
  archiveMb: z.number().int().nonnegative(),
  computeReleasedAt: z.string().nullable(),
  imageMb: z.number().int().nonnegative(),
  requestedCpuMilli: z.number().int().nonnegative(),
  requestedRamMb: z.number().int().nonnegative(),
  rootfsBaseMb: z.number().int().nonnegative(),
  runtimeMb: z.number().int().nonnegative(),
  status: z.string(),
  totalMb: z.number().int().nonnegative(),
  workspaceBaseMb: z.number().int().nonnegative(),
});

export const dashboardChannelsListResponseSchema = z
  .object({ channels: z.array(dashboardUserChannelSummarySchema) })
  .meta({ ref: "DashboardChannelsListResponse" });

export const dashboardCreateChannelRequestSchema = z
  .object({ displayName: z.string().trim().min(1).max(256) })
  .meta({ ref: "DashboardCreateChannelRequest" });

export const dashboardCreateChannelResponseSchema = z
  .object({
    createdAt: z.string(),
    displayName: z.string(),
    id: z.string(),
    organizationId: z.string(),
    provenance: z.enum(["issue_workspace", "manual", "teams_bind"]),
  })
  .meta({ ref: "DashboardCreateChannelResponse" });

export const dashboardChannelEnvironmentParamSchema = z.object({
  channelId: z.string().trim().min(1),
});

export const dashboardAttachChannelEnvironmentRequestSchema = z
  .object({
    actorProcessToolsEnabled: z.boolean().default(true),
    cpuCount: z.number().positive(),
    egressPolicy: channelEnvironmentEgressPolicySchema.default({
      mode: "deny",
    }),
    idleStopAfterHours: dashboardIdleStopAfterHoursSchema.default(168),
    maxConcurrentSpawns: z.number().int().positive().default(5),
    maxSpawnWallSeconds: z.number().int().positive().nullable().default(86_400),
    previewPorts: channelEnvironmentPreviewPortsSchema.default([]),
    profileId: z.string().trim().min(1).optional(),
    ramMB: z.number().int().positive(),
    retentionDaysAfterChannelKill: z
      .number()
      .int()
      .positive()
      .nullable()
      .default(30),
    storageMB: z.number().int().positive(),
  })
  .meta({ ref: "DashboardAttachChannelEnvironmentRequest" });

export const dashboardChannelEnvironmentProfileUnavailableReasonSchema = z.enum(
  [
    "capacity_exhausted",
    "image_unavailable",
    "missing_required_capability",
    "no_eligible_node",
  ],
);

export const dashboardChannelEnvironmentProfileResourcesSchema = z.object({
  caps: z.object({
    maxCpuMilli: z.number().int().positive(),
    maxPidsLimit: z.number().int().positive().nullable().optional(),
    maxRamMb: z.number().int().positive(),
    maxRootfsMb: z.number().int().positive().nullable(),
    maxWorkspaceMb: z.number().int().positive(),
  }),
  defaults: z.object({
    cpuMilli: z.number().int().positive(),
    pidsLimit: z.number().int().positive().nullable().optional(),
    ramMb: z.number().int().positive(),
    rootfsMb: z.number().int().positive().nullable(),
    workspaceMb: z.number().int().positive(),
  }),
});

export const dashboardChannelEnvironmentProfileSchema = z
  .object({
    displayName: z.string(),
    imageDigest: z.string().nullable(),
    imageReadiness: z.literal("unknown"),
    imageRef: z.string(),
    profileId: z.string(),
    requiredCapabilities: z.array(sandboxNodeCapabilitySchema),
    resources: dashboardChannelEnvironmentProfileResourcesSchema.nullable(),
    selectable: z.boolean(),
    toolchains: z.array(z.string()),
    unavailableMessage: z.string().nullable(),
    unavailableReason:
      dashboardChannelEnvironmentProfileUnavailableReasonSchema.nullable(),
    version: z.number().int().positive(),
  })
  .meta({ ref: "DashboardChannelEnvironmentProfile" });

export const dashboardListChannelEnvironmentProfilesResponseSchema = z
  .object({ profiles: z.array(dashboardChannelEnvironmentProfileSchema) })
  .meta({ ref: "DashboardListChannelEnvironmentProfilesResponse" });

export const dashboardPatchChannelEnvironmentRequestSchema = z
  .object({
    actorProcessToolsEnabled: z.boolean().optional(),
    confirmRestart: z.boolean().optional(),
    cpuCount: z.number().positive().optional(),
    egressPolicy: channelEnvironmentEgressPolicySchema.optional(),
    idleStopAfterHours: dashboardIdleStopAfterHoursSchema.optional(),
    maxConcurrentSpawns: z.number().int().positive().optional(),
    maxSpawnWallSeconds: z.number().int().positive().nullable().optional(),
    previewPorts: channelEnvironmentPreviewPortsSchema.optional(),
    ramMB: z.number().int().positive().optional(),
    retentionDaysAfterChannelKill: z
      .number()
      .int()
      .positive()
      .nullable()
      .optional(),
    storageMB: z.number().int().positive().optional(),
  })
  .meta({ ref: "DashboardPatchChannelEnvironmentRequest" });

export const dashboardChannelEnvironmentRepositoryPermissionSchema = z.enum([
  "read",
  "write",
]);

export const dashboardChannelEnvironmentRepositoryStatusSchema = z.enum([
  "pending",
  "cloning",
  "ready",
  "refreshing",
  "errored",
  "detach_pending",
  "detached",
]);

/**
 * Typed reason a `detach_pending` attachment has not reached `detached`.
 * Mirrors `CHANNEL_ENVIRONMENT_REPOSITORY_DETACH_BLOCKERS` in persistence;
 * duplicated here because `@arti/schemas` stays off the persistence dependency.
 */
export const dashboardChannelEnvironmentRepositoryDetachBlockerSchema = z.enum([
  "active_spawns",
  "checkout_absent",
  "checkout_not_git",
  "dirty_checkout",
  "unpushed_branches",
  "dirty_and_unpushed",
  "verification_failed",
  "removal_failed",
  "runtime_unavailable",
  "queued",
]);

/**
 * Server-derived operator action that can resolve a blocked detach. The client
 * never manufactures eligibility from message text.
 */
export const dashboardChannelEnvironmentRepositoryDetachResolutionSchema =
  z.enum(["acknowledge_absent_checkout"]);

export const dashboardDeleteChannelEnvironmentRepositoryQuerySchema = z.object({
  resolution:
    dashboardChannelEnvironmentRepositoryDetachResolutionSchema.optional(),
});

export const dashboardRefreshChannelEnvironmentRepositoryRequestSchema = z
  .object({ force: z.boolean().optional() })
  .default({})
  .meta({ ref: "DashboardRefreshChannelEnvironmentRepositoryRequest" });

export const dashboardPatchChannelEnvironmentRepositoryRequestSchema = z
  .object({ branch: gitBranchNameSchema })
  .meta({ ref: "DashboardPatchChannelEnvironmentRepositoryRequest" });

export const dashboardChannelEnvironmentRepositoryRefreshResultSchema = z
  .object({
    autostashCommitSha: z.string().nullable(),
    autostashRef: z.string().nullable(),
    autostashRetained: z.boolean(),
    autostashRetainedReason: z
      .enum(["apply_conflict", "drop_failed", "merge_conflict"])
      .nullable(),
    autostashShortId: z.string().nullable(),
    autostashToken: z.string().nullable(),
    cleanFiles: z.array(z.string()),
    conflictedFiles: z.array(z.string()),
    force: z.boolean(),
    hasConflicts: z.boolean(),
    newCommitSha: z.string(),
    oldCommitSha: z.string(),
    requestedBy: z.string().nullable(),
  })
  .meta({ ref: "DashboardChannelEnvironmentRepositoryRefreshResult" });

export const dashboardChannelEnvironmentRepositorySystemEgressRuleSchema = z
  .object({
    host: z.string().trim().min(1),
    includeSubdomains: z.literal(false),
    ports: z.array(z.number().int().positive().max(65_535)),
    protocols: z.array(z.literal("https")),
    providerId: z.string().trim().min(1),
    reason: z.literal("attached_repository"),
    remoteSlug: z.string().trim().min(1),
    repositoryId: z.string().trim().min(1),
  })
  .meta({ ref: "DashboardChannelEnvironmentRepositorySystemEgressRule" });

const dashboardProfileBundleSystemEgressRuleSchema = z.object({
  bundleName: z.string().trim().min(1),
  host: z.string().trim().min(1),
  includeSubdomains: z.boolean(),
  ports: z.array(z.number().int().positive().max(65_535)),
  protocols: z.array(z.literal("https")),
  reason: z.literal("profile_egress_bundle"),
});

export const dashboardChannelEnvironmentSystemEgressRuleSchema = z
  .discriminatedUnion("reason", [
    dashboardChannelEnvironmentRepositorySystemEgressRuleSchema,
    dashboardProfileBundleSystemEgressRuleSchema,
  ])
  .meta({ ref: "DashboardChannelEnvironmentSystemEgressRule" });

/**
 * Branch-create (PR-A): how the attachment picks its branch. `existing` is
 * the historical behaviour (`branch` null ⇒ repository default). `create`
 * asks the orchestrator to create `branch` from `baseBranch` exactly once
 * under `idempotencyKey`; the browser never supplies a SHA or an identity.
 * Requires `permission: "write"` — the created branch is the only one that
 * gains push authority automatically.
 */
export const dashboardRepositoryBranchCollisionPolicySchema = z
  .enum(["fail", "reuse_existing", "suffix"])
  .meta({ ref: "DashboardRepositoryBranchCollisionPolicy" });

export const dashboardRepositoryBranchSelectionSchema = z
  .discriminatedUnion("mode", [
    z
      .object({
        branch: gitBranchNameSchema.nullable(),
        mode: z.literal("existing"),
      })
      .strict(),
    // Strict (C2): the browser never supplies a sha or requester identity;
    // an unknown field here is refused, not stripped.
    z
      .object({
        baseBranch: gitBranchNameSchema,
        branch: creatableGitBranchNameSchema,
        collisionPolicy:
          dashboardRepositoryBranchCollisionPolicySchema.default("fail"),
        idempotencyKey: z.uuid(),
        mode: z.literal("create"),
      })
      .strict(),
  ])
  .meta({ ref: "DashboardRepositoryBranchSelection" });

export const dashboardAttachChannelEnvironmentRepositoryRequestSchema = z
  .object({
    /** Takes precedence over the legacy `defaultBranch` when present. */
    branchSelection: dashboardRepositoryBranchSelectionSchema.optional(),
    defaultBranch: gitBranchNameSchema.nullable().optional(),
    permission: dashboardChannelEnvironmentRepositoryPermissionSchema,
    providerId: z.string().trim().min(1),
    repositoryId: z.string().trim().min(1),
  })
  .meta({ ref: "DashboardAttachChannelEnvironmentRepositoryRequest" });

/** Outcome of a `create` branch selection, echoed on the attach response. */
export const dashboardRepositoryBranchProvisionSchema = z
  .object({
    baseBranch: z.string(),
    operationId: z.string(),
    outcome: z.enum(["created", "created_suffixed", "reused_existing"]),
    /** True when the created branch was linked to the attachment and push
     * was enabled in the same transaction (C5). */
    pushEnabled: z.boolean(),
    /** Typed reason push could NOT be enabled (attachment still exists). */
    pushEnablementError: z.string().nullable(),
    requestedBranch: z.string(),
    resolvedBranch: z.string(),
  })
  .meta({ ref: "DashboardRepositoryBranchProvision" });

export const dashboardChannelEnvironmentRepositoryParamSchema = z.object({
  channelId: z.string().trim().min(1),
  repoAttachmentId: z.string().trim().min(1),
});

export const dashboardChannelEnvironmentPreviewParamSchema = z.object({
  channelId: z.string().trim().min(1),
  publicPort: z.coerce.number().int().min(1).max(65_535),
});

export const dashboardPreviewPublicConfigReasonSchema = z.enum([
  "host_missing",
  "invalid_url",
  "ok",
  "protocol_defaulted",
]);

export const dashboardChannelEnvironmentSchema = z
  .object({
    actorProcessToolsEnabled: z.boolean(),
    channelEnvironmentId: z.string(),
    channelId: z.string(),
    containerId: z.string().nullable(),
    containerImage: z.string(),
    cpuCount: z.number(),
    egressAuditCounters: z.object({
      allowed: z.number().int().nonnegative(),
      blocked: z.number().int().nonnegative(),
      proxyUnavailable: z.number().int().nonnegative(),
    }),
    egressPolicy: channelEnvironmentEgressPolicySchema,
    idleStopAfterHours: z.number().nullable(),
    lastActivityAt: z.string(),
    lastEgressBlockedAt: z.string().nullable(),
    lastEgressBlockedTarget: z.string().nullable(),
    lastErrorAt: z.string().nullable(),
    lastErrorMessage: z.string().nullable(),
    maxConcurrentSpawns: z.number(),
    maxSpawnWallSeconds: z.number().nullable(),
    previewPorts: channelEnvironmentPreviewPortsSchema,
    previewPublicConfigReason: dashboardPreviewPublicConfigReasonSchema
      .optional()
      .default("host_missing"),
    previewPublicConfigured: z.boolean().optional().default(false),
    profileId: z.string(),
    profileImageDigest: z.string().nullable(),
    profileImageRef: z.string(),
    profileVersion: z.number().int().positive(),
    ramMB: z.number(),
    reservationAccounting:
      dashboardChannelEnvironmentReservationAccountingSchema,
    reservationHeld:
      dashboardChannelEnvironmentReservationHeldSchema.nullable(),
    restartStartedAt: z.string().nullable(),
    retainedUntil: z.string().nullable(),
    retentionDaysAfterChannelKill: z.number().nullable(),
    snapshotRef: z.string().nullable(),
    spawnAccounting: dashboardChannelEnvironmentSpawnAccountingSchema,
    status: z.string(),
    storageMB: z.number(),
    systemEgressRules: z.array(
      dashboardChannelEnvironmentSystemEgressRuleSchema,
    ),
    volumeName: z.string(),
    workspacePath: z.string().nullable(),
    workspaceQuotaProjectId: z.number().int().nullable(),
  })
  .meta({ ref: "DashboardChannelEnvironment" });

export const dashboardChannelEnvironmentRepositorySchema = z
  .object({
    branchChangeError: z.string().nullable().optional(),
    /** Present only on the response to a `create` branch selection. */
    branchProvision: dashboardRepositoryBranchProvisionSchema
      .nullable()
      .optional(),
    branchProvisionOperationId: z.string().nullable().optional(),
    channelEnvironmentId: z.string(),
    checkoutPath: z.string(),
    createdAt: z.string(),
    defaultBranch: z.string().nullable(),
    detachBlocker:
      dashboardChannelEnvironmentRepositoryDetachBlockerSchema.nullable(),
    detachResolution:
      dashboardChannelEnvironmentRepositoryDetachResolutionSchema.nullable(),
    id: z.string(),
    lastCommitSha: z.string().nullable(),
    lastError: z.string().nullable(),
    lifecycleSeq: z.number().int().nonnegative(),
    organizationId: z.string(),
    pendingBranch: z.string().nullable().optional(),
    pendingReason: z
      .enum(["credential_retry", "egress_requires_restart", "provider_retry"])
      .nullable(),
    permission: dashboardChannelEnvironmentRepositoryPermissionSchema,
    providerId: z.string(),
    remoteSlug: z.string(),
    repositoryDefaultBranch: z.string().nullable(),
    repositoryId: z.string(),
    status: dashboardChannelEnvironmentRepositoryStatusSchema,
    updatedAt: z.string(),
  })
  .meta({ ref: "DashboardChannelEnvironmentRepository" });

const dashboardChannelEnvironmentRepositoryResponseSchema =
  dashboardChannelEnvironmentRepositorySchema
    .required({ branchChangeError: true, pendingBranch: true })
    .meta({ ref: "DashboardChannelEnvironmentRepositoryResponse" });

export const dashboardListChannelEnvironmentRepositoriesResponseSchema = z
  .object({
    repositories: z.array(dashboardChannelEnvironmentRepositoryResponseSchema),
  })
  .meta({ ref: "DashboardListChannelEnvironmentRepositoriesResponse" });

export const dashboardAttachChannelEnvironmentRepositoryResponseSchema =
  dashboardChannelEnvironmentRepositoryResponseSchema.meta({
    ref: "DashboardAttachChannelEnvironmentRepositoryResponse",
  });

export const dashboardPatchChannelEnvironmentRepositoryResponseSchema =
  dashboardChannelEnvironmentRepositoryResponseSchema.meta({
    ref: "DashboardPatchChannelEnvironmentRepositoryResponse",
  });

export const dashboardRefreshChannelEnvironmentRepositoryResponseSchema =
  dashboardChannelEnvironmentRepositoryResponseSchema
    .extend({
      refreshResult:
        dashboardChannelEnvironmentRepositoryRefreshResultSchema.optional(),
    })
    .meta({ ref: "DashboardRefreshChannelEnvironmentRepositoryResponse" });

export const dashboardLaunchChannelEnvironmentPreviewResponseSchema = z
  .object({
    expiresAt: z.string(),
    publicPort: z.number().int().min(1).max(65_535),
    url: z.string().url(),
  })
  .meta({ ref: "DashboardLaunchChannelEnvironmentPreviewResponse" });

export const dashboardDeleteChannelEnvironmentRepositoryResponseSchema = z
  .object({
    attachment: dashboardChannelEnvironmentRepositorySchema.nullable(),
    status: z.enum(["deleted", "detach_pending"]),
  })
  .meta({ ref: "DashboardDeleteChannelEnvironmentRepositoryResponse" });

export const dashboardAttachChannelEnvironmentResponseSchema = z
  .object({
    channel_environment_id: z.string(),
    status: z.enum(["attaching", "running"]),
  })
  .meta({ ref: "DashboardAttachChannelEnvironmentResponse" });

export const dashboardGetChannelEnvironmentResponseSchema =
  dashboardChannelEnvironmentSchema.meta({
    ref: "DashboardGetChannelEnvironmentResponse",
  });

export const dashboardRestartChannelEnvironmentResponseSchema =
  dashboardChannelEnvironmentSchema.meta({
    ref: "DashboardRestartChannelEnvironmentResponse",
  });

export const dashboardDeleteChannelEnvironmentResponseSchema = z
  .object({ status: z.enum(["deleted", "gone", "hibernated"]) })
  .meta({ ref: "DashboardDeleteChannelEnvironmentResponse" });

export const dashboardArchiveChannelEnvironmentResponseSchema = z
  .object({ status: z.literal("hibernated") })
  .meta({ ref: "DashboardArchiveChannelEnvironmentResponse" });

export const dashboardLostHostRecoveryIneligibleReasonSchema = z.enum([
  "environment_not_found",
  "not_hibernated",
  "environment_deleted",
  "recovery_already_prepared",
  "recovery_operation_conflict",
  "checkpoint_unavailable",
  "checkpoint_manifest_unavailable",
  "checkpoint_manifest_invalid",
  "storage_backend_unsupported",
  "storage_generation_unavailable",
  "hibernation_pin_missing",
  "hibernation_not_canonical",
  "active_process_spawns",
  "lifecycle_operation_in_flight",
  "reservation_missing",
  "reservation_conflict",
  "reservation_not_hibernated",
  "reservation_compute_not_released",
  "reservation_capacity_already_released",
  "reservation_pin_mismatch",
  "reservation_inventory_mismatch",
  "replacement_inventory_missing",
  "replacement_inventory_conflict",
  "replacement_inventory_not_ready",
  "replacement_host_not_proven",
  "replacement_node_lease_mismatch",
  "preview_snapshot_changed",
]);

const dashboardLostHostRecoverySnapshotSchema = z.object({
  checkpointCreatedAt: z.string().datetime(),
  checkpointGeneration: z.number().int().positive(),
  checkpointId: z.string().min(1),
  checkpointRef: z.string().min(1),
  checkpointSequence: z.number().int().positive(),
  environmentLifecycleSeq: z.number().int().nonnegative(),
  environmentOwnerBootId: z.string().min(1),
  environmentOwnerLeaseEpoch: z.number().int().nonnegative(),
  environmentOwnerNodeId: z.string().min(1),
  hibernateOperationId: z.string().min(1),
  hibernatePhase: z.literal("source_removed"),
  hibernatedAt: z.string().datetime(),
  inventoryAdoptionCompletedAt: z.string().datetime(),
  inventoryBootId: z.string().min(1),
  inventoryHostIdentity: z.string().min(1),
  inventoryId: z.string().min(1),
  inventoryNodeId: z.string().min(1),
  inventoryOwnerLeaseEpoch: z.number().int().nonnegative(),
  reservationCapacityReleasedAt: z.null(),
  reservationComputeReleasedAt: z.string().datetime(),
  reservationExpectedNodeBootId: z.string().min(1),
  reservationExpectedNodeOwnerLeaseEpoch: z.number().int().nonnegative(),
  reservationHeldRootfsMb: z.number().int().nonnegative(),
  reservationHeldWorkspaceMb: z.number().int().nonnegative(),
  reservationHostIdentity: z.string().min(1),
  reservationId: z.string().min(1),
  reservationInventoryId: z.string().min(1),
  reservationNodeId: z.string().min(1),
  reservationReleasedAt: z.null(),
  reservationStatus: z.literal("hibernated"),
  reservationStorageAdoptionGeneration: z.number().int().nonnegative(),
  sourceHostIdentity: z.string().min(1),
  sourceNodeId: z.string().min(1),
});

export const dashboardLostHostRecoveryPreviewResponseSchema = z
  .discriminatedUnion("eligible", [
    z.object({
      eligible: z.literal(false),
      reason: dashboardLostHostRecoveryIneligibleReasonSchema,
    }),
    z.object({
      checkpoint: z.object({
        createdAt: z.string().datetime(),
        generation: z.number().int().nonnegative(),
        id: z.string().min(1),
        ref: z.string(),
        sequence: z.number().int().positive(),
      }),
      eligible: z.literal(true),
      idempotencyKey: z.string().uuid(),
      operationId: z.string().uuid(),
      releasedStorageMb: z.number().int().nonnegative(),
      replacement: z.object({ hostIdentity: z.string(), nodeId: z.string() }),
      snapshot: dashboardLostHostRecoverySnapshotSchema,
      source: z.object({ hostIdentity: z.string(), nodeId: z.string() }),
    }),
  ])
  .meta({ ref: "DashboardLostHostRecoveryPreviewResponse" });

export const dashboardPrepareLostHostRecoveryRequestSchema = z
  .object({
    acknowledgedPermanentLoss: z.literal(true),
    idempotencyKey: z.string().uuid(),
    operationId: z.string().uuid(),
    snapshot: dashboardLostHostRecoverySnapshotSchema,
  })
  .meta({ ref: "DashboardPrepareLostHostRecoveryRequest" });

export const dashboardPrepareLostHostRecoveryResponseSchema = z
  .object({
    channelEnvironmentId: z.string(),
    idempotent: z.boolean(),
    operationId: z.string(),
    status: z.enum(["completed", "stopped"]),
  })
  .meta({ ref: "DashboardPrepareLostHostRecoveryResponse" });

export const dashboardPatchChannelEnvironmentResponseSchema = z
  .object({
    applied: dashboardChannelEnvironmentSchema,
    requiresReplacement: z.boolean(),
    requiresRestart: z.boolean(),
    restartInProgress: z.boolean().optional(),
    runningSpawnsAffected: z.number().int().nonnegative(),
  })
  .meta({ ref: "DashboardPatchChannelEnvironmentResponse" });

export const dashboardTeamsBindingTokenParamSchema = z.object({
  id: z.string().trim().min(1),
});

export const dashboardTeamsBindingTokenResponseSchema = z
  .object({ expiresAt: z.string(), token: z.string(), url: z.string() })
  .meta({ ref: "DashboardTeamsBindingTokenResponse" });

const dashboardActorTokenUsageSchema = z.object({
  cacheWriteTokens: z.number(),
  cachedInputTokens: z.number(),
  inputTokens: z.number(),
  outputTokens: z.number(),
});

const effectiveSettingSource = z.union([
  z.literal("instance"),
  z.literal("template"),
]);

export const dashboardReasoningEffortSchema = z.enum([
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
]);

export const dashboardIssueWorkspaceTemplateDefaultSchema = z.enum([
  "lead",
  "member",
]);

const minCompactContextGrowthThreshold = 1000;

export const dashboardCreateActorTemplateBodySchema = z
  .object({
    avatarSeed: actorAvatarSeedSchema,
    compactContextGrowthThreshold: z
      .number()
      .int()
      .min(minCompactContextGrowthThreshold)
      .optional(),
    consolidationSchedule: actorConsolidationScheduleSchema.optional(),
    description: z.string().trim().max(1000).optional(),
    heartbeatIntervalS: z.number().int().min(5).max(3600).optional(),
    identityPrompt: z.string().trim().optional(),
    isSingleton: z.boolean().optional(),
    issueWorkspaceDefault: dashboardIssueWorkspaceTemplateDefaultSchema
      .nullable()
      .optional(),
    /**
     * Org MCP server ids attached to this template. An empty array attaches
     * none; omitting the field leaves attachments alone.
     */
    mcpServerIds: z.array(z.string().trim().min(1).max(64)).max(32).optional(),
    modelRef: resolvableActorModelRefSchema.optional(),
    name: z.string().trim().min(1).max(256),
    reasoningEffort: dashboardReasoningEffortSchema.nullable().optional(),
    tickIntervalMs: actorTickIntervalMsSchema.optional(),
    unarmedParkGraceS: z.number().int().positive().optional(),
  })
  .meta({ ref: "DashboardCreateActorTemplateBody" });

export const dashboardUpdateActorTemplateBodySchema = z
  .object({
    avatarSeed: actorAvatarSeedSchema.optional(),
    compactContextGrowthThreshold: z
      .number()
      .int()
      .min(minCompactContextGrowthThreshold)
      .optional(),
    consolidationSchedule: actorConsolidationScheduleSchema
      .nullable()
      .optional(),
    description: z.string().trim().max(1000).nullable().optional(),
    heartbeatIntervalS: z.number().int().min(5).max(3600).nullable().optional(),
    identityPrompt: z.string().trim().nullable().optional(),
    isSingleton: z.boolean().optional(),
    issueWorkspaceDefault: dashboardIssueWorkspaceTemplateDefaultSchema
      .nullable()
      .optional(),
    /**
     * Replaces the attachment set. `null` detaches everything, an array sets
     * exactly that list, and omitting it leaves attachments untouched.
     */
    mcpServerIds: z
      .array(z.string().trim().min(1).max(64))
      .max(32)
      .nullable()
      .optional(),
    modelRef: resolvableActorModelRefSchema.nullable().optional(),
    name: z.string().trim().min(1).max(256).optional(),
    reasoningEffort: dashboardReasoningEffortSchema.nullable().optional(),
    tickIntervalMs: actorTickIntervalMsSchema.nullable().optional(),
    unarmedParkGraceS: z.number().int().positive().nullable().optional(),
  })
  .meta({ ref: "DashboardUpdateActorTemplateBody" });

export const dashboardPatchActorInstanceBodySchema = z
  .object({
    compactContextGrowthThresholdOverride: z
      .number()
      .int()
      .min(minCompactContextGrowthThreshold)
      .nullable()
      .optional(),
    consolidationScheduleOverride: actorConsolidationScheduleSchema
      .nullable()
      .optional(),
    heartbeatIntervalSOverride: z
      .number()
      .int()
      .min(5)
      .max(3600)
      .nullable()
      .optional(),
    identityPrompt: z.string().nullable().optional(),
    /**
     * Per-actor override of the template's MCP servers. `null` goes back to
     * inheriting; `[]` attaches none even when the template attaches some.
     */
    mcpServerIdsOverride: z
      .array(z.string().trim().min(1).max(64))
      .max(32)
      .nullable()
      .optional(),
    modelRef: resolvableActorModelRefSchema.nullable().optional(),
    reasoningEffortOverride: dashboardReasoningEffortSchema
      .nullable()
      .optional(),
    tickIntervalMs: actorTickIntervalMsSchema.nullable().optional(),
  })
  .meta({ ref: "DashboardPatchActorInstanceBody" });

export const dashboardSpawnActorInstanceBodySchema = z
  .object({
    avatarSeed: actorAvatarSeedSchema.optional(),
    displayName: z.string().trim().min(1).max(256),
    identityPrompt: z.string().trim().optional(),
    modelRef: resolvableActorModelRefSchema.optional(),
    sharedChannelId: z.string().trim().min(1).optional(),
    shared_channel_id: z.string().trim().min(1).optional(),
    templateId: z.string().trim().min(1),
  })
  .superRefine((value, context) => {
    if (
      value.sharedChannelId !== undefined &&
      value.shared_channel_id !== undefined &&
      value.sharedChannelId !== value.shared_channel_id
    ) {
      context.addIssue({
        code: "custom",
        message: "must match sharedChannelId",
        path: ["shared_channel_id"],
      });
    }
  })
  .meta({ ref: "DashboardSpawnActorInstanceBody" });

export const dashboardActorOverridesSchema = z.object({
  compactContextGrowthThreshold: z.number().int().positive(),
  compactContextGrowthThresholdOverride: z.number().int().positive().nullable(),
  compactContextGrowthThresholdSource: effectiveSettingSource,
  consolidationSchedule: z.string().nullable(),
  consolidationScheduleOverride: z.string().nullable(),
  consolidationScheduleSource: effectiveSettingSource.nullable(),
  contextWindowTokens: z.number().int().positive(),
  heartbeatIntervalS: z.number().int().positive().nullable(),
  heartbeatIntervalSOverride: z.number().int().positive().nullable(),
  heartbeatIntervalSSource: effectiveSettingSource.nullable(),
  reasoningEffort: dashboardReasoningEffortSchema.nullable(),
  reasoningEffortOverride: dashboardReasoningEffortSchema.nullable(),
  reasoningEffortSource: effectiveSettingSource.nullable(),
  systemCacheTtl: z.enum(["5m", "1h"]).nullable(),
  systemCacheTtlSource: effectiveSettingSource.nullable(),
});

export const dashboardActorInstanceSchema = z
  .object({
    /**
     * Lifetime cache totals filtered to `step_kind='actor_wake'`. cacheHitPct
     * uses Formula B: `cache_read / (cache_read + no_cache)` — the same
     * efficacy denominator the per-wake `reportCacheStats` event uses, so
     * lifetime and per-activation cache % share one definition. Null when the
     * actor has no actor_wake steps yet.
     */
    actorWakeCacheStats: z
      .object({
        cacheHitPct: z.number().nullable(),
        cacheReadTokens: z.number().int().nonnegative(),
        cacheWriteTokens: z.number().int().nonnegative(),
        noCacheTokens: z.number().int().nonnegative(),
      })
      .nullable(),
    avatarSeed: z.string(),
    channelLockWaiting: z.boolean(),
    channels: z.array(dashboardActorChannelSchema),
    contextGrowthSinceEvolution: z.number().int().nonnegative(),
    contextWindowSize: z.number().int().nonnegative(),
    createdAt: z.string(),
    currentRunId: z.string().nullable(),
    displayName: z.string(),
    evolution: z.number().int().nonnegative(),
    id: z.string(),
    identityPrompt: z.string().nullable(),
    inFlightStepKind: z
      .enum(["actor_wake", "external_compact_seed", "consolidation_only_wake"])
      .nullable(),
    inFlightWakeState: z
      .enum(["preparing", "lock_waiting", "llm_call", "between_steps"])
      .nullish()
      .default(null),
    /**
     * ISO timestamp of the most recent `step_kind='actor_wake'` step start.
     * Distinct from `lastParkedAt`, which any wake type updates (consolidation,
     * cache_warm, compact_seed). Thermal warm/cold reads from this so that
     * background passes — which use a different `system_prompt_sha` and don't
     * refresh the act-phase prompt cache — never paint the badge warm.
     */
    lastActorWakeAt: z.string().datetime().nullable(),
    lastParkedAt: z.string().datetime().nullable(),
    modelRef: z.string().nullable(),
    organizationId: z.string(),
    overrides: dashboardActorOverridesSchema.nullable(),
    runCount: z.number(),
    spawnedBy: z.string().nullable(),
    status: z.string(),
    systemPromptEditedAt: z.string().datetime().nullable(),
    templateId: z.string().nullable(),
    tickIntervalMs: z.number().int().positive().nullable(),
    tokenUsage: dashboardActorTokenUsageSchema,
    totalCostUsd: z.number().nullable(),
    updatedAt: z.string(),
    wakeBlockedAt: z.string().datetime().nullable(),
    wakeBlockedConsecutiveFailureCount: z
      .number()
      .int()
      .nonnegative()
      .nullable(),
    wakeBlockedError: z.string().nullable(),
    wakeBlockedReason: z.string().nullable(),
    wakeBlockedTriggerChannelMessageId: z.string().nullable(),
    wakeBlockedUntil: z.string().datetime().nullable(),
  })
  .meta({ ref: "DashboardActorInstance" });

export const dashboardActorInstancesListResponseSchema = z
  .array(dashboardActorInstanceSchema)
  .meta({ ref: "DashboardActorInstancesListResponse" });

/**
 * An org-registered MCP server as returned to the dashboard. Header values are
 * absent by construction: a secret header reports only that it is set, so no
 * response, log or browser devtools panel can hand back a credential an admin
 * typed once. Plain header values come back, because their whole point is being
 * readable.
 */
export const dashboardOrgMcpServerHeaderSchema = z
  .object({
    is_secret: z.boolean(),
    name: z.string(),
    value: z.string().nullable(),
    value_set: z.boolean(),
  })
  .meta({ ref: "DashboardOrgMcpServerHeader" });

export const dashboardOrgMcpServerSchema = z
  .object({
    created_at: z.string(),
    description: z.string().nullable(),
    display_name: z.string(),
    headers: z.array(dashboardOrgMcpServerHeaderSchema),
    id: z.string(),
    slug: z.string(),
    status: z.enum(["active", "disabled"]),
    transport: z.enum(["http"]),
    updated_at: z.string(),
    url: z.string(),
  })
  .meta({ ref: "DashboardOrgMcpServer" });

export const dashboardOrgMcpServersListResponseSchema = z
  .object({ servers: z.array(dashboardOrgMcpServerSchema) })
  .meta({ ref: "DashboardOrgMcpServersListResponse" });

/**
 * A header on the way in. `value` is required on create and, on update, means
 * "replace this one"; omitting it keeps whatever is stored, which is how an
 * admin edits a URL without re-typing two API keys.
 */
const orgMcpServerHeaderInput = z.object({
  is_secret: z.boolean().optional(),
  name: z
    .string()
    .trim()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/u, "invalid_header_name"),
  /**
   * 2048 is the plaintext column width, and it also keeps a sealed value inside
   * the 8000-char ref column: sealing inflates by roughly 1.8x, so a limit set
   * to the ref width would pass validation and then die on truncation at the
   * database with a 500. Real API keys are two orders of magnitude smaller.
   */
  value: z.string().max(2048).optional(),
});

export const dashboardOrgMcpServerCreateRequestSchema = z
  .object({
    description: z.string().trim().max(1000).nullable().optional(),
    display_name: z.string().trim().min(1).max(128),
    headers: z.array(orgMcpServerHeaderInput).max(32).optional(),
    slug: z
      .string()
      .trim()
      .toLowerCase()
      .regex(/^[a-z][a-z0-9_]{1,63}$/u, "invalid_slug"),
    status: z.enum(["active", "disabled"]).optional(),
    transport: z.enum(["http"]).optional(),
    url: z.string().trim().url().max(2048),
  })
  .transform((d) => ({
    description: d.description ?? null,
    displayName: d.display_name,
    headers: d.headers ?? [],
    slug: d.slug,
    status: d.status ?? "active",
    transport: d.transport ?? "http",
    url: d.url,
  }))
  .meta({ ref: "DashboardOrgMcpServerCreateRequest" });

export const dashboardOrgMcpServerUpdateRequestSchema = z
  .object({
    description: z.string().trim().max(1000).nullable().optional(),
    display_name: z.string().trim().min(1).max(128).optional(),
    headers: z.array(orgMcpServerHeaderInput).max(32).optional(),
    status: z.enum(["active", "disabled"]).optional(),
    url: z.string().trim().url().max(2048).optional(),
  })
  .transform((d) => ({
    description: d.description,
    displayName: d.display_name,
    headers: d.headers,
    status: d.status,
    url: d.url,
  }))
  .meta({ ref: "DashboardOrgMcpServerUpdateRequest" });

export const dashboardOrgMcpServerIdParamSchema = z
  .object({ mcpServerId: z.string().trim().min(1).max(64) })
  .meta({ ref: "DashboardOrgMcpServerIdParam" });

export const dashboardOrgMcpServerResponseSchema = z
  .object({ server: dashboardOrgMcpServerSchema })
  .meta({ ref: "DashboardOrgMcpServerResponse" });

// ---------------------------------------------------------------------------
// Push broker — operator surface (single-app consolidation).
//
// The dashboard is the global-principal operator client of the orchestrator's
// /internal/push-broker/* routes; these schemas shape the org-scoped
// dashboard-facing mirror. Semantics exposed here are exactly what the broker
// enforces today (allowlist state, per-attachment push_enabled) — no
// policy_json affordances until enforcement lands. The push-plane installation
// is the existing App A installation recorded in the provider row metadata, so
// there is no installation registration surface or overview field.
// ---------------------------------------------------------------------------

export const dashboardPushBrokerAllowlistItemSchema = z
  .object({
    allowedBy: z.string().nullable(),
    providerId: z.string(),
    repositoryId: z.string(),
    revokedBy: z.string().nullable(),
    status: z.enum(["active", "revoked"]),
    updatedAt: z.string(),
  })
  .meta({ ref: "DashboardPushBrokerAllowlistItem" });

export const dashboardPushBrokerOverviewQuerySchema = z
  .object({ providerId: z.string().trim().min(1).max(128).optional() })
  .meta({ ref: "DashboardPushBrokerOverviewQuery" });

export const dashboardPushBrokerOverviewResponseSchema = z
  .object({ allowlist: z.array(dashboardPushBrokerAllowlistItemSchema) })
  .meta({ ref: "DashboardPushBrokerOverviewResponse" });

const dashboardPushBrokerRepositoryKeySchema = z.object({
  providerId: z.string().trim().min(1).max(128),
  repositoryId: z.string().trim().min(1).max(256),
});

/**
 * Member-level: activate the org allowlist row for a repository if absent,
 * on behalf of ONE workspace. Refused (409) when enabled policies exist in
 * other workspaces (`allowlist_activation_would_unlock_policies`) or the
 * row was revoked (`allowlist_revoked`) — both repaired by an org admin.
 */
export const dashboardPushBrokerAllowlistActivateRequestSchema =
  dashboardPushBrokerRepositoryKeySchema
    .extend({ channelEnvironmentId: z.string().trim().min(1).max(64) })
    .meta({ ref: "DashboardPushBrokerAllowlistActivateRequest" });

export const dashboardPushBrokerAllowlistActivateResponseSchema = z
  .object({
    outcome: z.enum(["activated", "already_active"]),
    repository: dashboardPushBrokerAllowlistItemSchema,
  })
  .meta({ ref: "DashboardPushBrokerAllowlistActivateResponse" });

/** Org-admin only: everything to review before an org-wide activation. */
export const dashboardPushBrokerAllowlistReviewQuerySchema =
  dashboardPushBrokerRepositoryKeySchema.meta({
    ref: "DashboardPushBrokerAllowlistReviewQuery",
  });

export const dashboardPushBrokerEnabledPolicySchema = z
  .object({
    attachmentBranch: z.string().nullable(),
    attachmentStatus: z.string().nullable(),
    channelEnvironmentId: z.string(),
    channelId: z.string().nullable(),
    channelName: z.string().nullable(),
    updatedAt: z.string(),
    updatedBy: z.string().nullable(),
  })
  .meta({ ref: "DashboardPushBrokerEnabledPolicy" });

export const dashboardPushBrokerAllowlistReviewResponseSchema = z
  .object({
    allowlist: dashboardPushBrokerAllowlistItemSchema.nullable(),
    enabledPolicies: z.array(dashboardPushBrokerEnabledPolicySchema),
  })
  .meta({ ref: "DashboardPushBrokerAllowlistReviewResponse" });

/** Org-admin only: explicit org-wide activate / revoke / re-authorize. */
export const dashboardPushBrokerAllowlistAdminRequestSchema =
  dashboardPushBrokerRepositoryKeySchema.meta({
    ref: "DashboardPushBrokerAllowlistAdminRequest",
  });

export const dashboardPushBrokerAllowlistAdminResponseSchema = z
  .object({ repository: dashboardPushBrokerAllowlistItemSchema })
  .meta({ ref: "DashboardPushBrokerAllowlistAdminResponse" });

export const dashboardPushBrokerPolicySchema = z
  .object({
    channelEnvironmentId: z.string(),
    providerId: z.string(),
    pushEnabled: z.boolean(),
    repositoryId: z.string(),
    revision: z.number(),
    updatedAt: z.string(),
    updatedBy: z.string().nullable(),
  })
  .meta({ ref: "DashboardPushBrokerPolicy" });

export const dashboardPushBrokerPoliciesQuerySchema = z
  .object({ channelEnvironmentId: z.string().trim().min(1).max(64) })
  .meta({ ref: "DashboardPushBrokerPoliciesQuery" });

export const dashboardPushBrokerPoliciesResponseSchema = z
  .object({ policies: z.array(dashboardPushBrokerPolicySchema) })
  .meta({ ref: "DashboardPushBrokerPoliciesResponse" });

export const dashboardPushBrokerPolicyUpdateRequestSchema = z
  .object({
    channelEnvironmentId: z.string().trim().min(1).max(64),
    providerId: z.string().trim().min(1).max(128),
    pushEnabled: z.boolean(),
    repositoryId: z.string().trim().min(1).max(256),
  })
  .meta({ ref: "DashboardPushBrokerPolicyUpdateRequest" });

export const dashboardPushBrokerPolicyUpdateResponseSchema = z
  .object({
    policy: dashboardPushBrokerPolicySchema.pick({
      channelEnvironmentId: true,
      providerId: true,
      pushEnabled: true,
      repositoryId: true,
      revision: true,
    }),
  })
  .meta({ ref: "DashboardPushBrokerPolicyUpdateResponse" });
